import {
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { createPortal } from "react-dom";
import {
  CLIENT_DEPOSIT_STATUS_IDS,
  resolveClientDepositStatus,
  type ClientDepositStatusId,
  type PushJobMode
} from "@tg/shared";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useShallow } from "zustand/react/shallow";
import { ChatAvatar } from "../../components/chat-avatar.js";
import { ContextMenuPositioner } from "../../components/context-menu.js";
import { LottieSticker } from "../../components/lottie-sticker.js";
import { pickByHash } from "../../lib/hash-pick.js";
import { formatAutoDeleteShort } from "../../lib/country.js";
import type { ChatViewModel, MessageViewModel } from "../../lib/store.js";
import { internalApi, type FrequentChatEntry } from "../../lib/internal-api.js";
import { pushToast } from "../../lib/toast.js";
import { kindForPreviewLabel, translationKey, useRealtimeStore } from "../../lib/store.js";
import { FOLDER_CHIPS_COMPACT_LIMIT, useUiPrefs } from "../../lib/ui-prefs.js";
import {
  formatDialogTime,
  formatExactDateTime,
  formatTime,
  timeZoneOffsetMinutes,
  timeZoneShortLabel,
  wallClockToUtcIso,
  type TimeZonePref
} from "../../lib/datetime.js";
import { visualRect } from "../../lib/zoom.js";
import { CLIENT_STATUS_META, formatDepositAge, useMinuteTick } from "../../lib/client-status.js";
import { messageStatusLabel } from "../../lib/message-status.js";
import { PushJobModal } from "../push/push-job-modal.js";
import { MessageSearchResults } from "./message-search-results.js";

const EMPTY_MESSAGES: MessageViewModel[] = [];
const EMPTY_FREQUENT: FrequentChatEntry[] = [];
const EMPTY_DRAFTS: Record<string, string> = {};

// Время в строке диалога («сегодня — 14:32 / неделя — Пн / старше — 12.05.26»)
// и границы суток считаются в часовом поясе саппорта: см. lib/datetime.ts.

// Иконка типа медиа в превью. Для чатов из снапшота kind неизвестен —
// восстанавливаем его по лейблу через kindForPreviewLabel (источник — store).
const PREVIEW_KIND_ICONS: Record<string, string> = {
  voice: "ic-mic",
  document: "ic-attach",
  video: "ic-videochat",
  videoNote: "ic-videochat"
};

const previewMediaIcon = (
  kind: string | undefined,
  text: string
): string | null => {
  const resolvedKind = kind ?? kindForPreviewLabel(text);
  return resolvedKind ? PREVIEW_KIND_ICONS[resolvedKind] ?? null : null;
};

// Превью в две части: цветной префикс («Черновик:» / имя отправителя в группе)
// и сам текст — как в мокапе (.msg .pre).
const buildPreviewParts = (
  chat: {
    isGroupChat: boolean;
    lastMessageText: string;
    hasNoMessages?: boolean;
    lastMessageSenderName?: string;
  },
  draftText?: string
): { prefix?: string; prefixKind?: "draft" | "sender"; text: string } => {
  if (typeof draftText === "string" && draftText.trim().length > 0) {
    return { prefix: "Черновик:", prefixKind: "draft", text: draftText.trim() };
  }

  // ?? "" — страховка от рассинхрона с бэкендом (undefined с провода)
  const lastMessageText = chat.lastMessageText ?? "";
  if (lastMessageText.length === 0) {
    // «Нет сообщений» — только когда воркер ПРОВЕРИЛ, что чат пуст. Пустое
    // превью само по себе означает лишь «TDLib ещё не отдал последнее
    // сообщение» (массово — сразу после сборки папки): раньше строка врала
    // «Нет сообщений» у чатов, в которых переписка есть.
    return { text: chat.hasNoMessages ? "Нет сообщений" : "" };
  }

  if (chat.isGroupChat && chat.lastMessageSenderName) {
    return {
      prefix: `${chat.lastMessageSenderName}:`,
      prefixKind: "sender",
      text: lastMessageText
    };
  }

  return { text: lastMessageText };
};

// Детерминированный градиент аватара из мокапа (g-*) по chatId.
const AVATAR_GRADIENTS = ["g-red", "g-orange", "g-violet", "g-green", "g-cyan", "g-blue", "g-pink"] as const;

export const avatarGradientClass = (seed: string): string =>
  pickByHash(seed, AVATAR_GRADIENTS);

// «✓» / «✓✓» для последнего исходящего в превью чата. Считается той же
// логикой, что и в ChatView (см. messageStatusLabel): если id ≤
// lastReadOutboxMessageId — read; иначе если время сообщения ≤
// lastReadOutboxAt — read (fallback для optimistic temp-id); иначе sent.
const computeChatReadStatus = (chat: ChatViewModel): "sent" | "read" | null => {
  if (!chat.lastMessageOutgoing || !chat.lastMessageId) {
    return null;
  }
  if (
    /^\d+$/.test(chat.lastMessageId) &&
    typeof chat.lastReadOutboxMessageId === "string" &&
    /^\d+$/.test(chat.lastReadOutboxMessageId) &&
    BigInt(chat.lastMessageId) <= BigInt(chat.lastReadOutboxMessageId)
  ) {
    return "read";
  }
  if (
    chat.lastReadOutboxAt &&
    chat.lastMessageOccurredAt &&
    chat.lastMessageOccurredAt <= chat.lastReadOutboxAt
  ) {
    return "read";
  }
  return "sent";
};

interface DialogListProps {
  searchQuery: string;
}

interface DialogListItemProps {
  chat: ChatViewModel;
  selected: boolean;
  folderChips: Array<{ id: string; title: string; kind: string }>;
  // DnD-флаги: draggable=true только для закреплённых в активной папке.
  // dropIndicator показывает, куда упадёт перетягиваемый чат (полоса сверху/снизу).
  draggable: boolean;
  isDragging: boolean;
  dropIndicator: "above" | "below" | null;
  // Активный listId (main / folder:xxx / archive / local:xxx). Иконка пина
  // рендерится только если чат закреплён именно в этой папке — иначе после
  // «Открепить» в подпапке значок оставался бы висеть за счёт пина в main.
  activeListId: string;
  // Закреплён ли чат именно в активной папке (для local:* — по пинам
  // gateway, для TDLib-листов — по pinnedListIds).
  pinnedInList: boolean;
  // Статус клиента по депозиту (запрос №22) — примитивами, чтобы memo
  // строки не ломался на каждом тике минуты.
  clientStatusId: ClientDepositStatusId | null;
  clientStatusAge: string;
  onOpen(chatId: string): void;
  onPreview(chatId: string, anchorX: number, anchorY: number): void;
  onContextMenu(chatId: string, x: number, y: number): void;
  onDragStart?(chatId: string): void;
  onDragOver?(chatId: string, position: "above" | "below"): void;
  onDragLeave?(chatId: string): void;
  onDrop?(chatId: string): void;
  onDragEnd?(): void;
}

const DialogListItem = memo(({
  chat,
  selected,
  folderChips,
  draggable,
  isDragging,
  dropIndicator,
  activeListId,
  pinnedInList,
  clientStatusId,
  clientStatusAge,
  onOpen,
  onPreview,
  onContextMenu,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd
}: DialogListItemProps) => {
  const draftText = useRealtimeStore((state) => state.draftsByChat[chat.chatId] ?? "");
  // Часовой пояс саппорта берём подпиской, а не пропом: memo-строка сама
  // перерисуется при смене настройки, и не нужно прокидывать его через
  // весь виртуализированный список.
  const timeZone: TimeZonePref = useUiPrefs((state) => state.timeZone);
  const folderChipsMode = useUiPrefs((state) => state.folderChipsMode);
  const visibleFolderChips =
    folderChipsMode === "compact"
      ? folderChips.slice(0, FOLDER_CHIPS_COMPACT_LIMIT)
      : folderChips;
  const hiddenFolderChips =
    folderChipsMode === "compact" ? folderChips.slice(FOLDER_CHIPS_COMPACT_LIMIT) : [];
  // Прежний потолок тегов (4) остаётся «полным» режимом — он и так был
  // подрезкой, просто без подсказки со скрытыми.
  const tagLimit = folderChipsMode === "compact" ? FOLDER_CHIPS_COMPACT_LIMIT : 4;
  const visibleTags = chat.internalTags.slice(0, tagLimit);
  const hiddenTags = chat.internalTags.slice(tagLimit);
  const preview = buildPreviewParts(chat, draftText);
  const hasDraft = draftText.trim().length > 0;
  const mediaIcon = hasDraft ? null : previewMediaIcon(chat.lastMessageKind, preview.text);
  // Галочки только когда последнее сообщение — моё исходящее и строку превью
  // не перетянули на себя typing/draft.
  const readStatus = chat.isTyping || hasDraft ? null : computeChatReadStatus(chat);

  const className = [
    "chat",
    selected ? "active" : "",
    draggable ? "chat--draggable" : "",
    isDragging ? "chat--dragging" : "",
    dropIndicator === "above" ? "chat--drop-above" : "",
    dropIndicator === "below" ? "chat--drop-below" : ""
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type="button"
      role="listitem"
      draggable={draggable}
      onClick={(event) => {
        if (event.altKey) {
          onPreview(chat.chatId, event.clientX, event.clientY);
          return;
        }
        onOpen(chat.chatId);
      }}
      onContextMenu={(event) => {
        event.preventDefault();
        onContextMenu(chat.chatId, event.clientX, event.clientY);
      }}
      onDragStart={(event) => {
        if (!draggable || !onDragStart) {
          event.preventDefault();
          return;
        }
        // Без setData drag в Firefox не стартует.
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", chat.chatId);
        onDragStart(chat.chatId);
      }}
      onDragOver={(event) => {
        if (!onDragOver) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
        // visualRect: clientY визуальный, rect в Safari логический.
        const rect = visualRect(event.currentTarget);
        const position = event.clientY - rect.top < rect.height / 2 ? "above" : "below";
        onDragOver(chat.chatId, position);
      }}
      onDragLeave={() => {
        onDragLeave?.(chat.chatId);
      }}
      onDrop={(event) => {
        if (!onDrop) return;
        event.preventDefault();
        onDrop(chat.chatId);
      }}
      onDragEnd={() => {
        onDragEnd?.();
      }}
      className={className}
    >
      <ChatAvatar
        title={chat.isSavedMessages ? "Избранное" : chat.title}
        avatarUrl={chat.avatarUrl}
        className={`avatar ${avatarGradientClass(chat.chatId)}`}
        savedMessages={chat.isSavedMessages ?? false}
      >
        {!chat.isSavedMessages && !chat.isGroupChat && chat.isBlockedByUser ? (
          <span
            className="status-dot status-dot--blocked"
            aria-label="Заблокировал"
            title="Собеседник заблокировал ваш аккаунт"
          />
        ) : !chat.isSavedMessages && !chat.isGroupChat && chat.isPeerOnline ? (
          <span className="status-dot status-dot--online" aria-label="В сети" title="В сети" />
        ) : null}
      </ChatAvatar>
      <div className="chat-body">
        <div className="chat-top">
          <div className="name">
            <span className="name-text">{chat.isSavedMessages ? "Избранное" : chat.title}</span>
            {chat.isMuted ? (
              <i
                className="ic ic-muted name-muted"
                title="Уведомления выключены"
                aria-label="Уведомления выключены"
              />
            ) : null}
          </div>
          <div className="chat-meta">
            {chat.autoDeleteTime ? (
              <span
                className="auto-del"
                title={`Автоудаление сообщений: ${formatAutoDeleteShort(chat.autoDeleteTime)}`}
                aria-label="Автоудаление включено"
              >
                ⏱{formatAutoDeleteShort(chat.autoDeleteTime)}
              </span>
            ) : null}
            {readStatus ? (
              <i
                className={
                  readStatus === "read" ? "ic ic-2checks chat-check" : "ic ic-1check chat-check"
                }
                aria-label={readStatus === "read" ? "Прочитано" : "Отправлено"}
                title={readStatus === "read" ? "Прочитано" : "Отправлено"}
              />
            ) : null}
            <span
              className="time"
              title={
                chat.lastActivityAt && new Date(chat.lastActivityAt).getTime() > 0
                  ? formatExactDateTime(chat.lastActivityAt, timeZone)
                  : undefined
              }
            >
              {formatDialogTime(chat.lastActivityAt, timeZone)}
            </span>
          </div>
        </div>
        <div className="chat-bottom">
          <div className="msg">
            {chat.isTyping ? (
              <span className="typing-indicator" aria-label="печатает">
                <span className="typing-indicator__dot" />
                <span className="typing-indicator__dot" />
                <span className="typing-indicator__dot" />
              </span>
            ) : (
              <>
                {preview.prefix ? (
                  <span className={preview.prefixKind === "draft" ? "pre draft" : "pre"}>
                    {preview.prefix}
                  </span>
                ) : null}
                {preview.prefix ? " " : null}
                {mediaIcon ? (
                  <i className={`ic ${mediaIcon} msg-media-icon`} aria-hidden="true" />
                ) : null}
                {/* dir="auto" изолирует превью: арабский текст с цифрами и
                    латиницей внутри читается так же, как в оригинальном TG,
                    и при этом не разворачивает всю строку диалога. */}
                <span dir="auto">{preview.text}</span>
              </>
            )}
          </div>
          <div className="end">
            {chat.unreadCount > 0 ? (
              <span className={chat.isMuted ? "badge muted" : "badge"}>{chat.unreadCount}</span>
            ) : chat.isMarkedAsUnread ? (
              <span
                className={chat.isMuted ? "badge badge--dot muted" : "badge badge--dot"}
                title="Помечен непрочитанным"
                aria-label="Помечен непрочитанным"
              />
            ) : pinnedInList ? (
              <i
                className="ic ic-pin2 pin-corner"
                title="Закреплённый чат"
                aria-label="Закреплённый чат"
              />
            ) : null}
          </div>
        </div>
        {/* Теги и папки — в одной строке (компактно), с общим переносом. */}
        <div className="dialog-list__meta">
        {clientStatusId ? (
          <span
            className={`client-status-badge client-status-badge--${clientStatusId}`}
            title={`${CLIENT_STATUS_META[clientStatusId].label}: последний депозит ${clientStatusAge} назад`}
          >
            <span aria-hidden="true">{CLIENT_STATUS_META[clientStatusId].emoji}</span>{" "}
            {CLIENT_STATUS_META[clientStatusId].label}
            {clientStatusAge ? <span className="client-status-badge__age"> · {clientStatusAge}</span> : null}
          </span>
        ) : null}
        {chat.internalTags.length > 0 ? (
          // Внутренние теги живут по тому же правилу, что и папки: в кратком
          // режиме видно 2, остальные — под «+N» с подсказкой.
          <div className="dialog-list__tags">
            {visibleTags.map((tag) => (
              <span key={tag} className="dialog-list__tag">
                {tag}
              </span>
            ))}
            {hiddenTags.length > 0 ? (
              <span
                className="dialog-list__tag dialog-list__tag--more"
                title={hiddenTags.join("\n")}
              >
                +{hiddenTags.length}
              </span>
            ) : null}
          </div>
        ) : null}
        {folderChips.length > 0 ? (
          // Компактный режим (как в оригинальном Telegram): видно 2 папки,
          // остальные — под «+N», список показывает подсказка при наведении.
          // Полный список включается настройкой «Папки у диалога».
          <div
            className={
              folderChipsMode === "compact"
                ? "dialog-list__folder-chips dialog-list__folder-chips--compact"
                : "dialog-list__folder-chips"
            }
          >
            {visibleFolderChips.map((folder) => (
              <span
                key={folder.id}
                className={
                  folder.kind === "archive"
                    ? "dialog-list__folder-chips-chip dialog-list__folder-chips-chip--archive"
                    : "dialog-list__folder-chips-chip"
                }
                title={folder.kind === "archive" ? "В архиве" : `Папка: ${folder.title}`}
              >
                <i className="ic ic-folder" aria-hidden="true" /> {folder.title}
              </span>
            ))}
            {hiddenFolderChips.length > 0 ? (
              <span
                className="dialog-list__folder-chips-chip dialog-list__folder-chips-chip--more"
                // Нативный title: подсказка всплывает сама через ~1–2 секунды
                // удержания курсора — ровно то поведение, что в ТЗ.
                title={hiddenFolderChips
                  .map((folder) => (folder.kind === "archive" ? "В архиве" : folder.title))
                  .join("\n")}
              >
                +{hiddenFolderChips.length}
              </span>
            ) : null}
          </div>
        ) : null}
        </div>
      </div>
    </button>
  );
});

DialogListItem.displayName = "DialogListItem";

interface ContextMenuState {
  chatId: string;
  x: number;
  y: number;
}

// ПКМ-меню (позиционер) стало общим с чатом — см. components/context-menu.tsx.

// memo: единственный prop — строка поиска, поэтому тумблеры меню/шитов и
// resize-стейт в App больше не перерисовывают весь список диалогов.
// Сколько показывать галочку «Пуш завершён» на вкладке папки.
const PUSH_DONE_MARK_MS = 10 * 60_000;

// Значок пуша (ИИ или скриптами) на вкладке папки (см. pushMarkByList). Клик
// открывает окно пуша этой папки — не переключая вкладку.

// Режим окна по клику на значок: режим последнего пуша этой папки.
const pushModeOfList = (accountId: string | null, listId: string): PushJobMode => {
  const jobs = Object.values(useRealtimeStore.getState().pushJobs)
    .filter((job) => job.accountId === accountId && job.listId === listId)
    .sort((left, right) => right.createdAt.localeCompare(left.createdAt));
  return jobs[0]?.mode ?? "ai";
};

const renderPushMark = (mark: string, onOpen: () => void) => {
  const [kind, status, sent, found] = mark.split("|");
  const title =
    kind === "running"
      ? `Пуш в работе${status === "paused" ? " (пауза Telegram)" : status === "scanning" ? " — сканируем папку" : ""} · отправлено ${sent} из ${found}. Нажмите, чтобы открыть`
      : kind === "queued"
        ? "Пуш в очереди — запустится сам. Нажмите, чтобы открыть"
        : `Пуш завершён · отправлено ${status}. Нажмите, чтобы открыть`;
  return (
    <span
      className={`folder__push folder__push--${kind}`}
      title={title}
      aria-label={title}
      onClick={(event) => {
        event.stopPropagation();
        onOpen();
      }}
    >
      <i
        className={`ic ${kind === "queued" ? "ic-clock" : kind === "done" ? "ic-check" : "ic-send"}`}
        aria-hidden="true"
      />
      {/* Состояния из ТЗ дословно: «В работе» / «В очереди» / «Завершено». */}
      <span>
        {kind === "running"
          ? `В работе${status === "sending" ? ` ${sent}/${found}` : ""}`
          : kind === "queued"
            ? "В очереди"
            : "Завершено"}
      </span>
    </span>
  );
};

export const DialogList = memo(({ searchQuery }: DialogListProps) => {
  const [previewChatId, setPreviewChatId] = useState<string | null>(null);
  // Точка, из которой «растёт» всплывашка превью (визуальные px, как у
  // ПКМ-меню) — превью теперь маленькое окошко у курсора, без затемнения.
  const [previewAnchor, setPreviewAnchor] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0
  });
  const previewBodyRef = useRef<HTMLDivElement | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const dialogLists = useRealtimeStore(useShallow((state) => state.dialogLists));
  const dialogListOrder = useRealtimeStore(useShallow((state) => state.dialogListOrder));
  const dialogListStateById = useRealtimeStore(useShallow((state) => state.dialogListStateById));
  const dialogHasMoreById = useRealtimeStore(useShallow((state) => state.dialogHasMoreById));
  const activeDialogListId = useRealtimeStore((state) => state.activeDialogListId);
  const listChatIds = useRealtimeStore(
    useShallow((state) => state.chatIdsByList[state.activeDialogListId] ?? [])
  );
  // Длина «Общих» для backoff фоновой докрутки, когда открыта папка.
  const mainListCount = useRealtimeStore((state) => state.chatIdsByList["main"]?.length ?? 0);
  const setActiveDialogList = useRealtimeStore((state) => state.setActiveDialogList);
  const loadMoreDialogs = useRealtimeStore((state) => state.loadMoreDialogs);
  const loadHistory = useRealtimeStore((state) => state.loadHistory);
  const chatsById = useRealtimeStore((state) => state.chats);
  const selectedChatId = useRealtimeStore((state) => state.selectedChatId);
  const selectChat = useRealtimeStore((state) => state.selectChat);
  const togglePinned = useRealtimeStore((state) => state.togglePinned);
  const movePinned = useRealtimeStore((state) => state.movePinned);
  const reorderPinned = useRealtimeStore((state) => state.reorderPinned);
  const toggleArchived = useRealtimeStore((state) => state.toggleArchived);
  const toggleMarkedAsUnread = useRealtimeStore((state) => state.toggleMarkedAsUnread);
  const setChatMuted = useRealtimeStore((state) => state.setChatMuted);
  const setListMembership = useRealtimeStore((state) => state.setListMembership);
  const currentAccountId = useRealtimeStore((state) => state.accountId);
  const wsUrl = useRealtimeStore((state) => state.wsUrl);
  // «Частые группы» — серверная настройка (см. store.frequentChatsByAccount):
  // они же whitelist пересылки для саппорта, поэтому назначает только админ.
  const frequentChats = useRealtimeStore(
    useShallow((state) =>
      state.accountId ? state.frequentChatsByAccount[state.accountId] ?? EMPTY_FREQUENT : EMPTY_FREQUENT
    )
  );
  const setAccountFrequentChat = useRealtimeStore((state) => state.setAccountFrequentChat);
  const isAdminRole = useRealtimeStore(
    (state) => state.appRole === "admin" || state.appRole === "geo_admin"
  );
  const createChatFolder = useRealtimeStore((state) => state.createChatFolder);
  const deleteChatFolder = useRealtimeStore((state) => state.deleteChatFolder);
  const updateChatFolder = useRealtimeStore((state) => state.updateChatFolder);
  const localFolders = useRealtimeStore((state) => state.localFolders);
  const refreshLocalFolders = useRealtimeStore((state) => state.refreshLocalFolders);
  // Статусы клиентов по депозитам (запрос №22): карта глобальная, пороги с
  // сервера; «сейчас» тикает раз в минуту, чтобы бейджи старели сами.
  const clientDeposits = useRealtimeStore((state) => state.clientDeposits);
  const clientStatusSettings = useRealtimeStore((state) => state.clientStatusSettings);
  const clientStatusFilter = useRealtimeStore((state) => state.clientStatusFilter);
  const setClientStatusFilter = useRealtimeStore((state) => state.setClientStatusFilter);
  const statusNow = useMinuteTick();
  const clientStatusOf = useCallback(
    (chat: ChatViewModel): ClientDepositStatusId | null => {
      if (!clientStatusSettings || !clientStatusSettings.enabled || chat.isGroupChat) {
        return null;
      }
      const telegramUserId = chat.telegramUserId;
      if (telegramUserId === undefined) {
        return null;
      }
      const record = clientDeposits[telegramUserId];
      return record
        ? resolveClientDepositStatus(record.lastDepositAt, clientStatusSettings, statusNow)
        : null;
    },
    [clientDeposits, clientStatusSettings, statusNow]
  );
  const clientStatusAgeOf = useCallback(
    (chat: ChatViewModel): string => {
      const record =
        chat.telegramUserId !== undefined ? clientDeposits[chat.telegramUserId] : undefined;
      return record ? formatDepositAge(record.lastDepositAt, statusNow) : "";
    },
    [clientDeposits, statusNow]
  );
  const appendChatsToLocalFolder = useRealtimeStore(
    (state) => state.appendChatsToLocalFolder
  );
  // null = модалка закрыта; "new" = создание; "local:<id>" = редактирование
  // локальной папки. TDLib-папки "folder:*" больше не редактируются через UI.
  const [folderEditor, setFolderEditor] = useState<null | "new" | string>(null);
  const [folderEditorTitle, setFolderEditorTitle] = useState("");
  const [folderEditorChatIds, setFolderEditorChatIds] = useState<Set<string>>(() => new Set());
  // Состав папки на момент открытия редактора: при сохранении шлём gateway
  // дельту (что отметили / что сняли), а не полный список — полный список
  // затирал бы чаты, добавленные тем временем из других вкладок.
  const folderEditorInitialIds = useRef<Set<string>>(new Set());
  // null = без лимита; число > 0 = ограничение количества чатов.
  const [folderEditorChatLimit, setFolderEditorChatLimit] = useState<number | null>(null);
  // Снапшот чатов для редактора папок. Сортировка 10k тайтлов с локалью
  // дорогая (localeCompare инлайном в JSX выполнялся на КАЖДЫЙ рендер и
  // вешал интерфейс при открытой модалке), поэтому: считаем только при
  // открытом редакторе; Intl.Collator вместо String#localeCompare;
  // пересортировка — только когда меняется НАБОР чатов (докатилась
  // пагинация), а не на каждое WS-событие. Слегка stale тайтлы в пикере
  // некритичны.
  const folderEditorChatsRef = useRef<{ count: number; value: ChatViewModel[] }>({
    count: -1,
    value: []
  });
  const folderEditorAllChats = useMemo(() => {
    if (folderEditor === null) {
      folderEditorChatsRef.current = { count: -1, value: [] };
      return folderEditorChatsRef.current.value;
    }
    const eligible = Object.values(chatsById).filter(
      // Отфильтровываем «болванки» — emptyChat(chatId) с title="Диалог" и
      // listIds=[]: они появляются, когда TDLib пушит событие на chatId до
      // его summary. Чаты из архива тоже не показываем.
      (chat) => chat.listIds.length > 0 && !chat.listIds.includes("archive")
    );
    if (eligible.length === folderEditorChatsRef.current.count) {
      return folderEditorChatsRef.current.value;
    }
    const collator = new Intl.Collator("ru");
    eligible.sort((a, b) => collator.compare(a.title, b.title));
    folderEditorChatsRef.current = { count: eligible.length, value: eligible };
    return eligible;
  }, [chatsById, folderEditor]);
  const localFolderChatIdsByList = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const folder of localFolders) {
      map[`local:${folder.folderId}`] = folder.includedChatIds;
    }
    return map;
  }, [localFolders]);
  const resolveFolderEditorChatTitle = useCallback(
    (chatId: string): string => {
      const title = chatsById[chatId]?.title;
      return title && title.trim().length > 0 ? title : `Чат ${chatId}`;
    },
    [chatsById]
  );
  const requestMainDialogs = useCallback(() => {
    loadMoreDialogs("main");
  }, [loadMoreDialogs]);
  // Шильдики папок для строки. Раньше массив собирался инлайном в JSX — новый
  // массив новых объектов на каждый рендер ломал memo у DialogListItem, и все
  // видимые строки перерисовывались на каждое WS-событие. Кэшируем по identity
  // массива chat.listIds: он стабилен, пока membership чата не меняется
  // (апдейты чата спредят прежнюю ссылку). WeakMap пересоздаётся при смене
  // самих папок или активного таба — это редкие события.
  const folderChipsCache = useMemo(
    () => new WeakMap<readonly string[], Array<{ id: string; title: string; kind: string }>>(),
    [dialogLists, activeDialogListId]
  );
  // Шильдики папок, СОЗДАННЫХ В КЛИЕНТЕ (local:*). Их состав живёт не в
  // chat.listIds (TDLib про такие папки не знает), поэтому чат из локальной
  // папки нигде не показывал свой тег: в «Новых» саппорт видел «свежий» чат
  // и понимал, что он чужой, только открыв его (ТЗ 2026-09-09). Строим
  // обратный индекс chatId → папки один раз на состав папок.
  const localFolderChipsByChatId = useMemo(() => {
    const map = new Map<string, Array<{ id: string; title: string; kind: string }>>();
    for (const folder of localFolders) {
      const listId = `local:${folder.folderId}`;
      if (listId === activeDialogListId) {
        continue;
      }
      const chip = { id: listId, title: folder.title, kind: "folder" };
      for (const chatId of folder.includedChatIds) {
        const chips = map.get(chatId);
        if (chips) {
          chips.push(chip);
        } else {
          map.set(chatId, [chip]);
        }
      }
    }
    return map;
  }, [localFolders, activeDialogListId]);
  // Готовые массивы шильдиков на чат. Кэш обязателен: DialogListItem —
  // memo-компонент, новый массив на каждый рендер перерисовывал бы все
  // видимые строки на каждое WS-событие. Ключ — chatId, валидность
  // проверяем по identity chat.listIds (она меняется только при смене
  // membership чата), сам кэш пересоздаётся при смене папок/таба.
  const mergedChipsCache = useMemo(
    () =>
      new Map<
        string,
        {
          listIds: readonly string[];
          chips: Array<{ id: string; title: string; kind: string }>;
        }
      >(),
    [folderChipsCache, localFolderChipsByChatId]
  );
  const folderChipsFor = useCallback(
    (
      chatId: string,
      listIds: string[]
    ): Array<{ id: string; title: string; kind: string }> => {
      let chips = folderChipsCache.get(listIds);
      if (!chips) {
        chips = listIds
          .filter(
            (listId) =>
              // показываем только реальные папки / архив, и только те, что НЕ
              // совпадают с текущим активным табом (иначе у каждого чата
              // шильдик про эту же папку — шумно)
              listId !== activeDialogListId &&
              (dialogLists[listId]?.kind === "folder" ||
                dialogLists[listId]?.kind === "archive")
          )
          .map((listId) => ({
            id: listId,
            title: dialogLists[listId]?.title ?? listId,
            kind: dialogLists[listId]?.kind ?? "folder"
          }));
        folderChipsCache.set(listIds, chips);
      }
      const localChips = localFolderChipsByChatId.get(chatId);
      if (!localChips || localChips.length === 0) {
        return chips;
      }
      const cached = mergedChipsCache.get(chatId);
      if (cached && cached.listIds === listIds) {
        return cached.chips;
      }
      const merged = [...chips, ...localChips];
      mergedChipsCache.set(chatId, { listIds, chips: merged });
      return merged;
    },
    [
      folderChipsCache,
      activeDialogListId,
      dialogLists,
      localFolderChipsByChatId,
      mergedChipsCache
    ]
  );
  // ПКМ-меню на табе папки (переименовать/удалить). Отдельно от
  // contextMenu для чатов, чтобы не путать варианты пунктов.
  // DnD вкладок папок (как в Telegram Desktop): тащим за вкладку, индикатор
  // «до/после» по положению курсора относительно середины вкладки.
  const [dragListId, setDragListId] = useState<string | null>(null);
  const [tabDropTarget, setTabDropTarget] = useState<{
    listId: string;
    position: "before" | "after";
  } | null>(null);
  const reorderDialogLists = useRealtimeStore((state) => state.reorderDialogLists);
  // Модалка «Пуш по папке» (раунд 46): доступна всем с доступом к аккаунту
  // (саппортам тоже), настройки промпта/модели — у менеджеров. Источник
  // правды — ui-prefs.pushLaunchTarget: контекстное меню на вкладке и пункт
  // ⌘M «ИИ-пропуш по папке» пишут в одно и то же поле.
  const pushLaunch = useUiPrefs((state) => state.pushLaunchTarget);
  // Метка ИИ-пуша на вкладке папки: идёт / в очереди / только что
  // завершился. Строка-примитив на папку — useShallow не перерисует список
  // на каждый push.jobUpdated, если по сути ничего не поменялось.
  const pushMarkByList = useRealtimeStore(
    useShallow((state) => {
      const marks: Record<string, string> = {};
      const now = Date.now();
      for (const job of Object.values(state.pushJobs)) {
        if (job.accountId !== state.accountId) continue;
        let mark: string | null = null;
        if (job.status === "scanning" || job.status === "sending" || job.status === "paused") {
          mark = `running|${job.status}|${job.sentCount}|${job.foundCount}`;
        } else if (job.status === "queued") {
          mark = "queued";
        } else if (
          job.status === "done" &&
          job.finishedAt &&
          now - new Date(job.finishedAt).getTime() < PUSH_DONE_MARK_MS
        ) {
          mark = `done|${job.sentCount}`;
        }
        // У папки может быть один незавершённый пуш; «в работе» важнее.
        if (mark && !(marks[job.listId]?.startsWith("running"))) {
          marks[job.listId] = mark;
        }
      }
      return marks;
    })
  );
  const openPushLaunch = useUiPrefs((state) => state.openPushLaunch);
  const closePushLaunch = useUiPrefs((state) => state.closePushLaunch);
  const [folderTabMenu, setFolderTabMenu] = useState<{
    listId: string;
    title: string;
    x: number;
    y: number;
  } | null>(null);
  // Конвертация папки Telegram ⇄ папка клиента (запрос №25): предпросмотр
  // с gateway → подтверждение → конвертация. Пока идёт — повторный клик
  // игнорируем: сага на сервере не параллелится по одной папке.
  const [folderConvertBusy, setFolderConvertBusy] = useState<string | null>(null);
  const convertFolder = useCallback(
    async (listId: string, title: string): Promise<void> => {
      if (!currentAccountId || folderConvertBusy) {
        return;
      }
      const toTelegram = listId.startsWith("local:");
      setFolderConvertBusy(listId);
      try {
        const preview = await internalApi.convertFolder(
          { accountId: currentAccountId, listId, dryRun: true },
          wsUrl
        );
        const lines = [
          toTelegram
            ? `Сделать папку «${title}» обычной папкой Telegram?`
            : `Сделать папку «${title}» папкой клиента?`,
          "",
          `Чатов для переноса: ${preview.chatCount}${
            preview.pinnedCount > 0 ? ` (закреплённых: ${preview.pinnedCount})` : ""
          }.`,
          preview.resultTitle !== preview.title ? `Новое название: «${preview.resultTitle}».` : "",
          ...preview.warnings.map((warning) => `⚠ ${warning}`),
          "",
          "Исходная папка будет удалена только после того, как новая создана и состав сверен."
        ].filter((line, index, all) => !(line === "" && all[index - 1] === ""));
        if (!window.confirm(lines.join("\n"))) {
          return;
        }
        const position = dialogListOrder.indexOf(listId);
        const result = await internalApi.convertFolder(
          {
            accountId: currentAccountId,
            listId,
            ...(position >= 0 ? { position } : {})
          },
          wsUrl
        );
        if (result.sourceDeleted === false) {
          pushToast(
            result.warnings[result.warnings.length - 1] ??
              "Новая папка создана, но исходную удалить не удалось.",
            "info"
          );
        } else {
          pushToast(
            toTelegram
              ? `Папка «${result.resultTitle}» теперь в Telegram (${result.chatCount} чатов).`
              : `Папка «${result.resultTitle}» теперь папка клиента (${result.chatCount} чатов).`
          );
        }
        if (result.newListId && activeDialogListId === listId) {
          // Новая папка клиента должна уже лежать в сторе, иначе вкладка
          // откроется пустой до прихода chat.localFoldersUpdated.
          if (result.newListId.startsWith("local:")) {
            await refreshLocalFolders();
          }
          setActiveDialogList(result.newListId);
        }
      } catch (cause) {
        pushToast(
          cause instanceof Error && cause.message
            ? cause.message
            : "Не удалось сконвертировать папку.",
          "error"
        );
      } finally {
        setFolderConvertBusy(null);
      }
    },
    [
      activeDialogListId,
      currentAccountId,
      dialogListOrder,
      folderConvertBusy,
      refreshLocalFolders,
      setActiveDialogList,
      wsUrl
    ]
  );
  // «Добрать чаты» — отдельная маленькая модалка над локальной папкой:
  // источник + количество + временные рамки, без списка чатов.
  const [folderTopUp, setFolderTopUp] = useState<{
    listId: string;
    title: string;
  } | null>(null);

  const deferredSearchQuery = useDeferredValue(searchQuery);
  const normalizedQuery = deferredSearchQuery.trim().toLowerCase();
  const searchableDraftsByChat = useRealtimeStore((state) =>
    normalizedQuery.length > 0 ? state.draftsByChat : EMPTY_DRAFTS
  );
  const previewMessages = useRealtimeStore((state) =>
    previewChatId ? (state.messagesByChat[previewChatId] ?? EMPTY_MESSAGES) : EMPTY_MESSAGES
  );
  const previewIsLoading = useRealtimeStore((state) =>
    previewChatId ? (state.historyLoadingByChat[previewChatId] ?? false) : false
  );
  const previewReachedStart = useRealtimeStore((state) =>
    previewChatId ? (state.historyReachedStartByChat[previewChatId] ?? false) : false
  );
  // Перевод в превью: тот же механизм, что и в открытом чате —
  // translatedChats/translationsByMessage из стора.
  const previewTranslated = useRealtimeStore((state) =>
    previewChatId ? Boolean(state.translatedChats[previewChatId]) : false
  );
  const previewTranslationLang = useRealtimeStore((state) =>
    previewChatId
      ? (state.translationLanguageByChat[previewChatId] ?? "ru").toUpperCase()
      : "RU"
  );
  const translationsByMessage = useRealtimeStore((state) => state.translationsByMessage);
  const setChatTranslation = useRealtimeStore((state) => state.setChatTranslation);
  const loadOlderHistory = useRealtimeStore((state) => state.loadOlderHistory);
  const foldersLayout = useUiPrefs((state) => state.foldersLayout);
  const countMutedInFolderBadges = useUiPrefs((state) => state.countMutedInFolderBadges);
  const previewTimeZone: TimeZonePref = useUiPrefs((state) => state.timeZone);
  // Папки по линиям (раунд 46, опционально): саппорт видит папки без
  // привязки и папки своей линии; без линии — только непривязанные.
  // Менеджеры видят всё (и управляют привязками через ПКМ по папке).
  const folderLines = useRealtimeStore((state) => state.folderLines);
  const setFolderLineBinding = useRealtimeStore((state) => state.setFolderLineBinding);
  const folderLineFilterActive = Boolean(folderLines?.enabled) && !isAdminRole;
  const folderVisibleForLine = useCallback(
    (listId: string): boolean => {
      if (!folderLineFilterActive || !folderLines) {
        return true;
      }
      const bound = folderLines.bindings[listId];
      if (!bound || bound.length === 0) {
        return true;
      }
      return folderLines.myLineId !== null && bound.includes(folderLines.myLineId);
    },
    [folderLineFilterActive, folderLines]
  );
  const availableLists = useMemo(
    () => {
      const ordered = dialogListOrder
        .map((dialogListId) => dialogLists[dialogListId])
        .filter((dialogList): dialogList is NonNullable<typeof dialogList> => Boolean(dialogList));

      const all = ordered.length > 0 ? ordered : Object.values(dialogLists);
      return folderLineFilterActive
        ? all.filter((dialogList) => folderVisibleForLine(dialogList.id))
        : all;
    },
    [dialogListOrder, dialogLists, folderLineFilterActive, folderVisibleForLine]
  );
  // Активная папка спряталась фильтром линий (привязку сменил админ) —
  // уходим в «Общие», иначе список показывает невидимую вкладку.
  useEffect(() => {
    if (!folderLineFilterActive) {
      return;
    }
    if (activeDialogListId !== "main" && !folderVisibleForLine(activeDialogListId)) {
      setActiveDialogList("main");
    }
  }, [activeDialogListId, folderLineFilterActive, folderVisibleForLine, setActiveDialogList]);
  // Источники автонабора: перебирает их воркер, поэтому только «Общие» и
  // папки Telegram. Обычно нужна папка «Новые» — она приезжает как
  // folder:<id>. Локальные папки источником быть не могут: их состав SPA и
  // так знает целиком, набирать из них нечего.
  const autoPickSources = useMemo(
    () =>
      availableLists
        .filter((dialogList) => dialogList.kind === "main" || dialogList.kind === "folder")
        .map((dialogList) => ({ id: dialogList.id, title: dialogList.title })),
    [availableLists]
  );
  // Sum of unread messages across all chats currently known in each list.
  // Rendered next to folder tabs without clamping (per ТЗ §1.1: «чтобы число
  // отображалось полностью», в отличие от десктопного TG c «99+»).
  // Считается в сторе троттл-подпиской (unreadTotals) — раньше цикл по всем
  // спискам × всем чатам выполнялся тут на каждое WS-событие.
  const unreadChatsByList = useRealtimeStore((state) => state.unreadTotals.chatsByList);
  const unmutedChatsByList = useRealtimeStore(
    (state) => state.unreadTotals.unmutedChatsByList
  );
  // Серверные счётчики TDLib (updateUnreadChatCount/updateUnreadMessageCount)
  // — авторитетны для main/archive/folder:*: клиентский счёт не видит чаты
  // папок за пределами загруженного окна, и бейджи папок показывали 0 при
  // непрочитанных. На бейджах — число непрочитанных ЧАТОВ (как в оригинальном
  // TG), точное, без «99+». Для local:* папок серверного счётчика нет —
  // fallback на клиентский счёт чатов.
  const serverUnreadByList = useRealtimeStore((state) => state.serverUnreadByList);
  const activeListState = dialogListStateById[activeDialogListId];
  const activeListHasMore = dialogHasMoreById[activeDialogListId] ?? true;
  // main-скаляры для авто-докрутки: эффект должен зависеть от них, а НЕ от целых
  // карт dialogListStateById/dialogHasMoreById — иначе каждый серверный
  // префетч-снапшот пересоздаёт карту, перезапускает эффект и сбрасывает таймер
  // до срабатывания (starvation). Скаляры меняются только при реальном сдвиге.
  const mainListState = dialogListStateById["main"];
  const mainListHasMore = dialogHasMoreById["main"] ?? true;
  const previewChat = previewChatId ? chatsById[previewChatId] : null;

  const chats = useMemo(
    () =>
      listChatIds
        .map((chatId) => chatsById[chatId])
        .filter((chat): chat is NonNullable<typeof chat> => {
          if (!chat) return false;
          // Подтверждённо пустые чаты (юзер удалил переписку с нами, остался
          // только наш черновик) TDLib держит в папках ради черновика —
          // прячем, пока в них нет сообщений/непрочитанного. Открытый чат
          // не трогаем, чтобы он не исчезал из-под курсора.
          if (
            chat.hasNoMessages &&
            chat.unreadCount === 0 &&
            !chat.isMarkedAsUnread &&
            chat.chatId !== selectedChatId
          ) {
            return false;
          }
          return true;
        }),
    [chatsById, listChatIds, selectedChatId]
  );

  // Серверный поиск (раунд 47): локальный фильтр ниже видит только чаты,
  // догруженные в SPA, — большинство юзеров аккаунта с 10k+ чатов в него не
  // попадает. Поэтому с задержкой спрашиваем worker (TDLib searchChats и
  // компания, см. chat.searchDialogs) и дописываем его результаты после
  // локальных. Результаты чужого/устаревшего запроса не показываем.
  const dialogSearch = useRealtimeStore((state) => state.dialogSearch);
  const searchDialogs = useRealtimeStore((state) => state.searchDialogs);
  const clearDialogSearch = useRealtimeStore((state) => state.clearDialogSearch);
  const serverQuery = deferredSearchQuery.trim();
  useEffect(() => {
    if (serverQuery.length < 2) {
      clearDialogSearch();
      return;
    }
    const timer = window.setTimeout(() => searchDialogs(serverQuery), 350);
    return () => window.clearTimeout(timer);
  }, [serverQuery, searchDialogs, clearDialogSearch]);
  const serverResultsFresh = dialogSearch.query === serverQuery && serverQuery.length >= 2;
  const serverSearchLoading = serverResultsFresh && dialogSearch.loading;
  // Общий поиск по сообщениям (запрос №29) живёт под списком найденных
  // чатов; галочка «Включая удалённые» — состояние вкладки, по умолчанию
  // выключена и не персистится.
  const messageSearchActive = serverQuery.length >= 2;
  const [includeDeletedInSearch, setIncludeDeletedInSearch] = useState(false);

  const searchedChats = useMemo(() => {
    if (normalizedQuery.length === 0) {
      return chats;
    }
    const local = chats.filter((chat) => {
      const haystack = `${chat.title} ${chat.lastMessageText} ${
        searchableDraftsByChat[chat.chatId] ?? ""
      } ${chat.internalTags.join(" ")} ${chat.chatId} ${
        chat.telegramUserId ?? ""
      }`.toLowerCase();
      return haystack.includes(normalizedQuery);
    });
    if (!serverResultsFresh || dialogSearch.chatIds.length === 0) {
      return local;
    }
    const seen = new Set(local.map((chat) => chat.chatId));
    const remote: typeof local = [];
    for (const chatId of dialogSearch.chatIds) {
      const chat = chatsById[chatId];
      if (chat && !seen.has(chatId)) {
        seen.add(chatId);
        remote.push(chat);
      }
    }
    return remote.length === 0 ? local : [...local, ...remote];
  }, [
    chats,
    chatsById,
    normalizedQuery,
    searchableDraftsByChat,
    serverResultsFresh,
    dialogSearch.chatIds
  ]);

  // Полоса фильтров по статусам: только включённые статусы со счётчиком по
  // текущей папке; скрыта, пока нет ни одного клиента со статусом (у
  // аккаунта без депозитной базы — ничего лишнего). Фильтр работает по
  // загруженным чатам папки (как и локальный поиск).
  const clientStatusCounts = useMemo(() => {
    const counts: Record<ClientDepositStatusId, number> = {
      hot: 0,
      warm: 0,
      cold: 0,
      sleeping: 0,
      marinade: 0
    };
    if (!clientStatusSettings?.enabled) {
      return counts;
    }
    for (const chat of chats) {
      const status = clientStatusOf(chat);
      if (status) {
        counts[status] += 1;
      }
    }
    return counts;
  }, [chats, clientStatusOf, clientStatusSettings]);
  const clientStatusChips = useMemo(
    () =>
      clientStatusSettings?.enabled
        ? CLIENT_DEPOSIT_STATUS_IDS.filter(
            (id) => clientStatusSettings.statuses.find((rule) => rule.id === id)?.enabled !== false
          )
        : [],
    [clientStatusSettings]
  );
  const clientStatusBarVisible =
    clientStatusChips.length > 0 &&
    CLIENT_DEPOSIT_STATUS_IDS.some((id) => clientStatusCounts[id] > 0);
  // Статус выключили/отключили в настройках — снимаем фильтр, иначе список
  // пустой без видимой причины.
  useEffect(() => {
    if (clientStatusFilter && !clientStatusChips.includes(clientStatusFilter)) {
      setClientStatusFilter(null);
    }
  }, [clientStatusChips, clientStatusFilter, setClientStatusFilter]);
  const filteredChats = useMemo(() => {
    if (!clientStatusFilter || !clientStatusBarVisible) {
      return searchedChats;
    }
    return searchedChats.filter((chat) => clientStatusOf(chat) === clientStatusFilter);
  }, [searchedChats, clientStatusFilter, clientStatusBarVisible, clientStatusOf]);

  const scrollTimeoutRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (scrollTimeoutRef.current !== null) {
        window.clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
    },
    []
  );

  // Фоновая авто-докрутка: чтобы саппорту не приходилось вручную крутить
  // «Общие» вниз для наполнения папок. Локальные папки фильтруют глобально
  // загруженные чаты, а те приходят из main — поэтому в фоне докручиваем main
  // до конца; а если открыта реальная TDLib-папка (folder:*), сначала её
  // (видимую пользователю). Сервер параллельно тоже префетчит main — это
  // подстраховка. loadMoreDialogs сам no-op при isLoading/exhausted; троттлим,
  // чтобы не забивать очередь воркера. Останавливается сам, когда hasMore=false.
  const autoLoadTimeoutRef = useRef<number | null>(null);
  // Backoff автодокрутки: у папки, которую прямо сейчас редактируют, TDLib
  // подолгу не подтверждает «fully loaded» → hasMore висит true, и цикл
  // «600мс → loadMore → не выросло → снова» молотил воркер listDialogs'ами
  // (в прод-логах 2026-07-31 — запрос каждые ~7с, prime до 39с, из-за чего
  // таймаутились команды добавления в папку). hasMore=false по «не выросло»
  // ставить НЕЛЬЗЯ (ломало пагинацию, см. раунды 8-13) — вместо этого просто
  // опрашиваем всё реже: 600мс → 1.2с → … → потолок 30с; сброс при росте
  // списка или смене вкладки.
  // Состояние backoff — ПО СПИСКАМ: таргет чередуется между открытой папкой
  // и «Общими» (чья очередь — зависит от hasMore обоих), и единая запись
  // сбрасывала счётчик на каждой смене таргета (прод 2026-10-15, вечер:
  // main у Yousef запрашивался 14 раз за 20с с тем же лимитом).
  const autoLoadBackoffRef = useRef(new Map<string, { lastCount: number; stalls: number }>());
  useEffect(() => {
    const clear = (): void => {
      if (autoLoadTimeoutRef.current !== null) {
        window.clearTimeout(autoLoadTimeoutRef.current);
        autoLoadTimeoutRef.current = null;
      }
    };
    clear();
    if (normalizedQuery.length > 0) {
      return;
    }
    const activeServerBacked =
      activeDialogListId === "main" || activeDialogListId.startsWith("folder:");
    const activeWantsLoad =
      Boolean(activeListState?.isLoaded) && !activeListState?.isLoading && activeListHasMore;
    const mainWantsLoad =
      Boolean(mainListState?.isLoaded) && !mainListState?.isLoading && mainListHasMore;
    const target =
      activeServerBacked && activeWantsLoad
        ? activeDialogListId
        : mainWantsLoad
          ? "main"
          : null;
    if (!target) {
      return;
    }
    // Длина именно ЦЕЛЕВОГО списка: раньше для фоновой докрутки «Общих» при
    // открытой папке тут было -1, застои не считались, и цикл шёл строго по
    // 600мс (прод 2026-10-15: две вкладки Yousef слали main раз в секунду).
    const currentCount = target === activeDialogListId ? listChatIds.length : mainListCount;
    // lastCount — МАКСИМУМ, который список достигал, а не последнее значение:
    // папка, где чат входит/выходит (позиции TDLib «дрожат»), меняла длину
    // N↔N+1 на каждом ответе, и это обнуляло backoff — цикл снова шёл с 600мс
    // (прод 2026-10-15, folder:113 у af429726, лимит 219k). Застой считаем
    // при планировании: эффект перезапускается на каждый ответ воркера.
    const backoffByList = autoLoadBackoffRef.current;
    let backoff = backoffByList.get(target);
    if (!backoff || currentCount > backoff.lastCount) {
      backoff = { lastCount: currentCount, stalls: 0 };
      backoffByList.set(target, backoff);
    } else {
      backoff.stalls += 1;
    }
    const delay = Math.min(600 * 2 ** backoff.stalls, 30_000);
    autoLoadTimeoutRef.current = window.setTimeout(() => {
      autoLoadTimeoutRef.current = null;
      loadMoreDialogs(target);
    }, delay);
    return clear;
  }, [
    activeDialogListId,
    activeListHasMore,
    activeListState?.isLoaded,
    activeListState?.isLoading,
    listChatIds,
    mainListCount,
    mainListHasMore,
    mainListState?.isLoaded,
    mainListState?.isLoading,
    normalizedQuery,
    loadMoreDialogs
  ]);

  const scrollParentRef = useRef<HTMLDivElement | null>(null);
  const virtualizer = useVirtualizer({
    count: filteredChats.length,
    getScrollElement: () => scrollParentRef.current,
    estimateSize: () => 70,
    overscan: 10,
    getItemKey: (index) => filteredChats[index]?.chatId ?? index
  });

  // Перестановка строк не должна двигать то, что видит саппорт (как в
  // оригинальном TG). Открытый чат после отправки уезжает в начало папки —
  // без компенсации все строки под ним сдвигались, а браузерный scroll
  // anchoring (теперь отключён в CSS) и вовсе прыгал в начало списка.
  // Якорь — первая видимая строка прошлого коммита, которая не уехала выше
  // вьюпорта; сдвигаем scrollTop на дельту её позиции.
  const rowPositionsRef = useRef<Map<string, { start: number; end: number }> | null>(null);
  useLayoutEffect(() => {
    const scrollEl = scrollParentRef.current;
    const previous = rowPositionsRef.current;
    const items = virtualizer.getVirtualItems();
    if (scrollEl && previous && scrollEl.scrollTop > 4 && normalizedQuery.length === 0) {
      const scrollTop = scrollEl.scrollTop;
      const viewportEnd = scrollTop + scrollEl.clientHeight;
      const candidates = [...previous.entries()]
        .filter(([, pos]) => pos.end > scrollTop && pos.start < viewportEnd)
        .sort((left, right) => left[1].start - right[1].start);
      for (const [key, pos] of candidates) {
        const newIndex = filteredChats.findIndex((chat) => chat.chatId === key);
        if (newIndex < 0) {
          continue;
        }
        const offset = virtualizer.getOffsetForIndex(newIndex, "start");
        if (!offset) {
          continue;
        }
        const newStart = offset[0];
        const size = pos.end - pos.start;
        // Строка уехала выше вьюпорта (это и есть открытый чат, ушедший в
        // начало) — якорем не годится, берём следующую видимую.
        if (newStart + size <= scrollTop && newStart < pos.start) {
          continue;
        }
        const delta = newStart - pos.start;
        if (Math.abs(delta) >= 1) {
          scrollEl.scrollTop = scrollTop + delta;
        }
        break;
      }
    }
    rowPositionsRef.current = new Map(
      items.map((item) => [String(item.key), { start: item.start, end: item.end }])
    );
  }, [filteredChats, normalizedQuery.length, virtualizer]);

  // «В начало списка» по повторному/двойному клику на папке.
  // Раньше здесь был scrollTo({ top: 0, behavior: "smooth" }) по самому
  // контейнеру, и в папках с сотнями чатов он не срабатывал: пока идёт
  // плавная прокрутка, виртуализатор домеряет пролетающие строки и на каждое
  // расхождение высоты компенсирует позицию собственным scrollTo — анимация
  // обрывается на первых же кадрах, а лента застревает в середине.
  // scrollToIndex(0) виртуализатора доводит до верха сам: он знает про
  // динамические высоты и повторяет попытку, пока смещение не устаканится.
  const scrollDialogsToTop = useCallback(() => {
    scrollParentRef.current?.scrollTo({ top: 0 });
    virtualizer.scrollToIndex(0, { align: "start" });
  }, [virtualizer]);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const listNode = event.currentTarget;
      if (scrollTimeoutRef.current !== null) {
        return;
      }
      scrollTimeoutRef.current = window.setTimeout(() => {
        scrollTimeoutRef.current = null;
        const distanceToBottom =
          listNode.scrollHeight - listNode.scrollTop - listNode.clientHeight;
        if (
          distanceToBottom < 320 &&
          !activeListState?.isLoading &&
          activeListHasMore &&
          normalizedQuery.length === 0
        ) {
          loadMoreDialogs(activeDialogListId);
        }
      }, 120);
    },
    [
      activeDialogListId,
      activeListHasMore,
      activeListState?.isLoading,
      loadMoreDialogs,
      normalizedQuery.length
    ]
  );

  useEffect(() => {
    if (!previewChatId || previewMessages.length > 0 || previewIsLoading) {
      return;
    }

    loadHistory(previewChatId, 40);
  }, [loadHistory, previewChatId, previewIsLoading, previewMessages.length]);

  useEffect(() => {
    if (
      !previewChatId ||
      previewIsLoading ||
      previewReachedStart ||
      previewMessages.length === 0 ||
      previewMessages.length >= 24
    ) {
      return;
    }

    loadOlderHistory(previewChatId, 40);
  }, [
    loadOlderHistory,
    previewChatId,
    previewIsLoading,
    previewMessages.length,
    previewReachedStart
  ]);

  useEffect(() => {
    if (!previewChatId) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setPreviewChatId(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [previewChatId]);

  useEffect(() => {
    if (!contextMenu) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setContextMenu(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [contextMenu]);

  const openChat = useCallback(
    (chatId: string): void => {
      selectChat(chatId);
    },
    [selectChat]
  );

  const previewChatDialog = useCallback(
    (chatId: string, anchorX: number, anchorY: number): void => {
      setPreviewAnchor({ x: anchorX, y: anchorY });
      setPreviewChatId(chatId);
    },
    []
  );

  // Превью открывается на ПОСЛЕДНИХ сообщениях, как чат в оригинальном ТГ.
  // Раньше тело превью оставалось на scrollTop=0 — показывало самое старое
  // из подгруженного окна.
  useLayoutEffect(() => {
    const body = previewBodyRef.current;
    if (!body || !previewChatId) {
      return;
    }
    body.scrollTop = body.scrollHeight;
  }, [previewChatId, previewMessages.length]);

  const openContextMenu = useCallback((chatId: string, x: number, y: number): void => {
    setContextMenu({ chatId, x, y });
  }, []);

  const activeLocalPins = useMemo(() => {
    if (!activeDialogListId.startsWith("local:")) {
      return null;
    }
    const folderId = activeDialogListId.slice("local:".length);
    return localFolders.find((folder) => folder.folderId === folderId)?.pinnedChatIds ?? [];
  }, [activeDialogListId, localFolders]);
  const contextMenuChat = contextMenu ? chatsById[contextMenu.chatId] : null;
  const contextMenuIsArchived = contextMenuChat?.listIds.includes("archive") ?? false;
  // Pin/unpin per-list: смотрим закреплён ли чат именно в активной папке,
  // а не вообще где-либо. Это нужно и для метки кнопки, и для команды.
  const contextMenuIsPinnedInActiveList = contextMenu
    ? activeLocalPins
      ? activeLocalPins.includes(contextMenu.chatId)
      : contextMenuChat?.pinnedListIds?.includes(activeDialogListId) ?? false
    : false;
  const contextMenuIsMarkedUnread =
    Boolean(contextMenuChat?.isMarkedAsUnread) || (contextMenuChat?.unreadCount ?? 0) > 0;
  // В меню чата показываем и локальные папки нашего клиента (local:*): без
  // них «Убрать из папки» было недоступно — состав локальных папок не входит
  // в chat.listIds, и текущая папка чата в меню просто не отмечалась.
  const contextMenuFolderChoices = useMemo(
    () =>
      availableLists.filter(
        (dialogList) =>
          dialogList.kind === "folder" ||
          dialogList.kind === "archive" ||
          dialogList.kind === "local"
      ),
    [availableLists]
  );
  // Позиция чата в упорядоченном массиве закреплённых чатов активного листа.
  // Нужна для скрытия «Выше»/«Ниже» на крайних чатах и для случая «закреплён
  // только один» — там перемещение бессмысленно.
  const pinnedOrderRaw = useMemo(() => {
    if (activeLocalPins) {
      return listChatIds.filter((id) => activeLocalPins.includes(id));
    }
    return listChatIds.filter((id) => {
      const chat = chatsById[id];
      return chat?.pinnedListIds?.includes(activeDialogListId) ?? false;
    });
  }, [listChatIds, chatsById, activeDialogListId, activeLocalPins]);
  // Ссылочно стабильный массив пинов: chatsById меняется на каждое WS-событие,
  // и без этого все DnD-колбэки (и memo-строки под ними) пересоздавались
  // посреди перетаскивания — узел перерисовывался, браузер молча обрывал
  // drag, а строка оставалась «полупрозрачной и мёртвой» до F5.
  const pinnedOrderStableRef = useRef<string[]>(pinnedOrderRaw);
  const pinnedOrderInActiveList = useMemo(() => {
    const previous = pinnedOrderStableRef.current;
    const same =
      previous.length === pinnedOrderRaw.length &&
      previous.every((id, index) => id === pinnedOrderRaw[index]);
    if (!same) {
      pinnedOrderStableRef.current = pinnedOrderRaw;
    }
    return pinnedOrderStableRef.current;
  }, [pinnedOrderRaw]);
  const pinnedIdsInActiveList = useMemo(
    () => new Set(pinnedOrderInActiveList),
    [pinnedOrderInActiveList]
  );
  // DnD-состояние закреплённых: что тащим, над чем висим и в какой половине
  // (выше/ниже середины строки). Полностью локальное, ничего не мажет в стор
  // до момента drop.
  const [dragChatId, setDragChatId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{
    chatId: string;
    position: "above" | "below";
  } | null>(null);

  const handleDragStart = useCallback((chatId: string) => {
    setDragChatId(chatId);
    setDropTarget(null);
  }, []);

  const handleDragOver = useCallback(
    (chatId: string, position: "above" | "below") => {
      if (!dragChatId || dragChatId === chatId) return;
      if (!pinnedIdsInActiveList.has(dragChatId)) return;
      // Наведение на НЕзакреплённый чат трактуем как «в конец закреплённого
      // блока»: индикатор рисуем под последним пином. Раньше жест молча
      // игнорировался и DnD казался сломанным.
      if (!pinnedIdsInActiveList.has(chatId)) {
        const lastPinned = pinnedOrderInActiveList[pinnedOrderInActiveList.length - 1];
        if (!lastPinned || lastPinned === dragChatId) return;
        setDropTarget((prev) =>
          prev && prev.chatId === lastPinned && prev.position === "below"
            ? prev
            : { chatId: lastPinned, position: "below" }
        );
        return;
      }
      setDropTarget((prev) =>
        prev && prev.chatId === chatId && prev.position === position
          ? prev
          : { chatId, position }
      );
    },
    [dragChatId, pinnedIdsInActiveList, pinnedOrderInActiveList]
  );

  const handleDragLeave = useCallback((chatId: string) => {
    setDropTarget((prev) => (prev?.chatId === chatId ? null : prev));
  }, []);

  const handleDrop = useCallback(
    (targetChatId: string) => {
      if (!dragChatId || dragChatId === targetChatId) {
        setDragChatId(null);
        setDropTarget(null);
        return;
      }
      if (!pinnedIdsInActiveList.has(dragChatId)) {
        setDragChatId(null);
        setDropTarget(null);
        return;
      }
      // Дроп на незакреплённый чат = «в конец закреплённого блока».
      if (!pinnedIdsInActiveList.has(targetChatId)) {
        reorderPinned(dragChatId, null, activeDialogListId);
        setDragChatId(null);
        setDropTarget(null);
        return;
      }
      const position = dropTarget?.chatId === targetChatId ? dropTarget.position : "above";
      const targetIdx = pinnedOrderInActiveList.indexOf(targetChatId);
      let insertBeforeId: string | null;
      if (position === "above") {
        insertBeforeId = targetChatId;
      } else {
        // «после target» = перед следующим закреплённым; если target —
        // последний, ставим в конец закреплённого блока (insertBeforeId = null).
        insertBeforeId = pinnedOrderInActiveList[targetIdx + 1] ?? null;
      }
      reorderPinned(dragChatId, insertBeforeId, activeDialogListId);
      setDragChatId(null);
      setDropTarget(null);
    },
    [
      activeDialogListId,
      dragChatId,
      dropTarget,
      pinnedIdsInActiveList,
      pinnedOrderInActiveList,
      reorderPinned
    ]
  );

  const handleDragEnd = useCallback(() => {
    setDragChatId(null);
    setDropTarget(null);
  }, []);

  // Страховка от «зависшего» DnD. Если dragend до элемента не долетел (узел
  // перерисован посреди перетаскивания, drop ушёл за окно/на другой экран),
  // вкладка папки оставалась полупрозрачной (folder--dragging) и выглядела
  // «серой, с которой ничего нельзя сделать» — жалоба саппортов 2026-09-23.
  // Слушаем bubble-фазу на window: React-обработчики drop/dragend на самих
  // вкладках срабатывают раньше и успевают прочитать dragListId.
  useEffect(() => {
    if (!dragListId && !dragChatId) {
      return;
    }
    const reset = () => {
      setDragListId(null);
      setTabDropTarget(null);
      setDragChatId(null);
      setDropTarget(null);
    };
    window.addEventListener("dragend", reset);
    window.addEventListener("drop", reset);
    window.addEventListener("mouseup", reset);
    return () => {
      window.removeEventListener("dragend", reset);
      window.removeEventListener("drop", reset);
      window.removeEventListener("mouseup", reset);
    };
  }, [dragListId, dragChatId]);

  const contextMenuPinIdx = contextMenu
    ? pinnedOrderInActiveList.indexOf(contextMenu.chatId)
    : -1;
  const contextMenuCanMoveUp =
    contextMenuIsPinnedInActiveList && contextMenuPinIdx > 0;
  const contextMenuCanMoveDown =
    contextMenuIsPinnedInActiveList &&
    contextMenuPinIdx >= 0 &&
    contextMenuPinIdx < pinnedOrderInActiveList.length - 1;

  return (
    <aside
      className={
        foldersLayout === "left" ? "dialog-list dialog-list--folders-left" : "dialog-list"
      }
    >
      {availableLists.length > 0 ? (
        <div
          className={foldersLayout === "left" ? "folders folders--left" : "folders"}
          role="tablist"
          aria-label="Папки диалогов"
          aria-orientation={foldersLayout === "left" ? "vertical" : "horizontal"}
        >
          {availableLists.map((dialogList) => {
            const state = dialogListStateById[dialogList.id];
            const serverEntry = serverUnreadByList[dialogList.id];
            // По умолчанию бейдж считает только чаты СО ЗВУКОМ — так считают
            // официальные клиенты. Из-за total-варианта у нас появлялись
            // счётчики на папках, где в оригинальном Telegram пусто (папка
            // целиком из muted-чатов). Кому нужен полный счёт — включает
            // «Считать чаты без звука» в настройках.
            // Серверный счётчик может протухнуть: TDLib не всегда шлёт
            // updateUnreadChatCount по папке после прочтения, и бейдж «1»
            // висел после ответа. Если папка загружена на клиенте целиком,
            // клиентский счёт не может быть НИЖЕ истины — берём минимум из
            // двух. Неполная папка — только сервер.
            const listFullyLoaded = dialogHasMoreById[dialogList.id] === false;
            const pickCount = (server: number | undefined, client: number): number =>
              server === undefined ? client : listFullyLoaded ? Math.min(server, client) : server;
            const totalUnread = pickCount(serverEntry?.chats, unreadChatsByList[dialogList.id] ?? 0);
            const unmutedUnread = pickCount(
              serverEntry?.unmutedChats,
              unmutedChatsByList[dialogList.id] ?? 0
            );
            // Логика официального клиента (раунд 48): пока в папке есть хоть
            // один непрочитанный чат со звуком — бейдж синий; если все
            // непрочитанные без звука — бейдж приглушённый с их числом.
            // Настройка «Считать чаты без звука» лишь добавляет muted-чаты
            // в число на синем бейдже.
            const unread = countMutedInFolderBadges
              ? totalUnread
              : unmutedUnread > 0
                ? unmutedUnread
                : totalUnread;
            const folderMuted = unread > 0 && unmutedUnread === 0;
            const unreadTitle =
              totalUnread !== unmutedUnread
                ? `${unmutedUnread} со звуком · всего ${totalUnread}`
                : undefined;
            // Локальные папки (kind === "local") поддерживают ПКМ-меню
            // (переименовать/удалить), а кнопка ⋮ на табе вызывает его же.
            const isCustomFolder =
              dialogList.kind === "folder" || dialogList.kind === "local";
            const tabDraggable = dialogList.kind !== "archive";
            const tabDrop =
              tabDropTarget?.listId === dialogList.id ? tabDropTarget.position : null;
            return (
              <div
                key={dialogList.id}
                className={[
                  "folder",
                  activeDialogListId === dialogList.id ? "active" : "",
                  dragListId === dialogList.id ? "folder--dragging" : "",
                  tabDrop === "before" ? "folder--drop-before" : "",
                  tabDrop === "after" ? "folder--drop-after" : ""
                ]
                  .filter(Boolean)
                  .join(" ")}
                draggable={tabDraggable}
                onDragStart={(event) => {
                  if (!tabDraggable) {
                    event.preventDefault();
                    return;
                  }
                  event.dataTransfer.effectAllowed = "move";
                  event.dataTransfer.setData("text/plain", `list:${dialogList.id}`);
                  setDragListId(dialogList.id);
                  setTabDropTarget(null);
                }}
                onDragOver={(event) => {
                  if (!dragListId || dragListId === dialogList.id) return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "move";
                  // Архив не двигается: дроп на него = «в конец перед архивом».
                  if (dialogList.kind === "archive") {
                    setTabDropTarget((prev) =>
                      prev && prev.listId === dialogList.id && prev.position === "before"
                        ? prev
                        : { listId: dialogList.id, position: "before" }
                    );
                    return;
                  }
                  const rect = event.currentTarget.getBoundingClientRect();
                  const position =
                    foldersLayout === "left"
                      ? event.clientY < rect.top + rect.height / 2
                        ? "before"
                        : "after"
                      : event.clientX < rect.left + rect.width / 2
                        ? "before"
                        : "after";
                  setTabDropTarget((prev) =>
                    prev && prev.listId === dialogList.id && prev.position === position
                      ? prev
                      : { listId: dialogList.id, position }
                  );
                }}
                onDragLeave={() => {
                  setTabDropTarget((prev) => (prev?.listId === dialogList.id ? null : prev));
                }}
                onDrop={(event) => {
                  if (!dragListId) return;
                  event.preventDefault();
                  const target = tabDropTarget;
                  setDragListId(null);
                  setTabDropTarget(null);
                  if (dragListId === dialogList.id) return;
                  const order = availableLists
                    .map((list) => list.id)
                    .filter((id) => id !== dragListId);
                  const targetIndex = order.indexOf(dialogList.id);
                  if (targetIndex < 0) return;
                  const insertAt =
                    target?.listId === dialogList.id && target.position === "after"
                      ? targetIndex + 1
                      : targetIndex;
                  order.splice(insertAt, 0, dragListId);
                  reorderDialogLists(order);
                }}
                onDragEnd={() => {
                  setDragListId(null);
                  setTabDropTarget(null);
                }}
              >
                <button
                  type="button"
                  className="folder__button"
                  onClick={() => {
                    // Повторный клик по УЖЕ активной папке — тоже в начало
                    // списка, как в оригинальном Telegram. Заодно страховка для
                    // двойного клика: он состоит из двух click'ов, и даже если
                    // dblclick где-то не долетит, второй клик отработает.
                    if (activeDialogListId === dialogList.id) {
                      scrollDialogsToTop();
                      return;
                    }
                    setActiveDialogList(dialogList.id);
                  }}
                  onDoubleClick={scrollDialogsToTop}
                  onContextMenu={(event) => {
                    if (!isCustomFolder) {
                      return;
                    }
                    event.preventDefault();
                    setFolderTabMenu({
                      listId: dialogList.id,
                      title: dialogList.title,
                      x: event.clientX,
                      y: event.clientY
                    });
                  }}
                  title={dialogList.title}
                >
                  {foldersLayout === "left" ? (
                    // Рейл в стиле Telegram Desktop: иконка над подписью.
                    // «Все чаты» — пузыри, архив — коробка, папки — папка.
                    <span className="folder__icon-wrap">
                      <i
                        className={`ic folder__icon ${
                          dialogList.kind === "main"
                            ? "ic-chats-filled"
                            : dialogList.kind === "archive"
                              ? "ic-archive-filled"
                              : "ic-folder-filled"
                        }`}
                        aria-hidden="true"
                      />
                      {unread > 0 ? (
                        // Точное число непрочитанных чатов, без «99+» —
                        // просьба заказчика 2026-08-06.
                        <span
                          className={
                            folderMuted
                              ? "cnt folder__icon-badge cnt--muted"
                              : "cnt folder__icon-badge"
                          }
                          aria-label={`${unread} непрочитанных чатов`}
                          {...(unreadTitle ? { title: unreadTitle } : {})}
                        >
                          {unread}
                        </span>
                      ) : null}
                    </span>
                  ) : null}
                  <span className="folder__label">{dialogList.title}</span>
                  {pushMarkByList[dialogList.id]
                    ? renderPushMark(pushMarkByList[dialogList.id]!, () =>
                        openPushLaunch(
                          dialogList.id,
                          dialogList.title,
                          pushModeOfList(currentAccountId, dialogList.id)
                        )
                      )
                    : null}
                  {foldersLayout === "left" ? null : unread > 0 ? (
                    // непрочитанные важнее индикатора загрузки — не маскируем
                    <span
                      className={folderMuted ? "cnt cnt--muted" : "cnt"}
                      aria-label={`${unread} непрочитанных`}
                      {...(unreadTitle ? { title: unreadTitle } : {})}
                    >
                      {unread}
                    </span>
                  ) : state?.isLoading ? (
                    <span className="cnt" aria-hidden="true">
                      …
                    </span>
                  ) : null}
                </button>
                {isCustomFolder ? (
                  <button
                    type="button"
                    className="folder__menu"
                    onClick={(event) => {
                      event.stopPropagation();
                      // Якорь меню — в визуальных px (как event.clientX).
                      const rect = visualRect(event.currentTarget);
                      setFolderTabMenu({
                        listId: dialogList.id,
                        title: dialogList.title,
                        x: rect.right,
                        y: rect.bottom
                      });
                    }}
                    title="Изменить или удалить папку"
                    aria-label={`Меню папки ${dialogList.title}`}
                  >
                    <i className="ic ic-more" aria-hidden="true" />
                  </button>
                ) : null}
              </div>
            );
          })}
          <button
            type="button"
            className="folder folder--new"
            onClick={() => {
              setFolderEditor("new");
              setFolderEditorTitle("");
              setFolderEditorChatIds(new Set());
              setFolderEditorChatLimit(null);
            }}
            title="Создать новую папку (только в этом клиенте, не уйдёт в TG)"
          >
            <i className="ic ic-plus" aria-hidden="true" /> Папка
          </button>
        </div>
      ) : null}

      <div
        className={[
          "dialog-list__layout",
          clientStatusBarVisible ? "dialog-list__layout--with-status-bar" : "",
          normalizedQuery.length > 0 ? "dialog-list__layout--with-search-option" : ""
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {clientStatusBarVisible ? (
          <div className="client-status-bar" role="group" aria-label="Фильтр по статусу клиента">
            <button
              type="button"
              className={clientStatusFilter === null ? "client-status-chip active" : "client-status-chip"}
              onClick={() => setClientStatusFilter(null)}
            >
              Все
            </button>
            {clientStatusChips.map((id) => (
              <button
                key={id}
                type="button"
                className={[
                  "client-status-chip",
                  `client-status-chip--${id}`,
                  clientStatusFilter === id ? "active" : ""
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => setClientStatusFilter(clientStatusFilter === id ? null : id)}
                title={`${CLIENT_STATUS_META[id].plural}: ${clientStatusCounts[id]} в этой папке`}
              >
                <span aria-hidden="true">{CLIENT_STATUS_META[id].emoji}</span>{" "}
                {CLIENT_STATUS_META[id].plural}
                {clientStatusCounts[id] > 0 ? (
                  <span className="client-status-chip__count">{clientStatusCounts[id]}</span>
                ) : null}
              </button>
            ))}
          </div>
        ) : null}
        {normalizedQuery.length > 0 ? (
          <label className="dialog-list__search-option">
            <input
              type="checkbox"
              checked={includeDeletedInSearch}
              onChange={(event) => setIncludeDeletedInSearch(event.target.checked)}
            />
            Включая удалённые сообщения
          </label>
        ) : null}
        <div
          ref={scrollParentRef}
          className="chatlist"
          role="list"
          onScroll={handleScroll}
        >
          {filteredChats.length > 0 ? (
            <div
              className="dialog-list__virtual"
              style={{
                height: `${virtualizer.getTotalSize()}px`,
                position: "relative",
                width: "100%"
              }}
            >
              {virtualizer.getVirtualItems().map((virtualRow) => {
                const chat = filteredChats[virtualRow.index];
                if (!chat) {
                  return null;
                }
                return (
                  <div
                    key={virtualRow.key}
                    data-index={virtualRow.index}
                    ref={virtualizer.measureElement}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      transform: `translateY(${virtualRow.start}px)`
                    }}
                  >
                    <DialogListItem
                      chat={chat}
                      selected={selectedChatId === chat.chatId}
                      folderChips={folderChipsFor(chat.chatId, chat.listIds)}
                      draggable={
                        // Тащить можно только закреплённые в активной папке
                        // и только когда выключен поиск (отфильтрованный
                        // список визуально не соответствует реальному порядку).
                        normalizedQuery.length === 0 &&
                        pinnedIdsInActiveList.has(chat.chatId) &&
                        pinnedOrderInActiveList.length > 1
                      }
                      isDragging={dragChatId === chat.chatId}
                      dropIndicator={
                        dropTarget?.chatId === chat.chatId ? dropTarget.position : null
                      }
                      activeListId={activeDialogListId}
                      pinnedInList={pinnedIdsInActiveList.has(chat.chatId)}
                      clientStatusId={clientStatusOf(chat)}
                      clientStatusAge={clientStatusAgeOf(chat)}
                      onOpen={openChat}
                      onPreview={previewChatDialog}
                      onContextMenu={openContextMenu}
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onDragEnd={handleDragEnd}
                    />
                  </div>
                );
              })}
            </div>
          ) : null}

          {chats.length === 0 &&
          (activeListState?.isLoading || !activeListState?.isLoaded) &&
          normalizedQuery.length === 0 ? (
            <div className="dialog-list__skeleton" aria-hidden="true">
              {Array.from({ length: 8 }).map((_, index) => (
                <div key={index} className="dialog-list__skeleton-row">
                  <div className="dialog-list__skeleton-avatar" />
                  <div className="dialog-list__skeleton-lines">
                    <div className="dialog-list__skeleton-line dialog-list__skeleton-line--title" />
                    <div className="dialog-list__skeleton-line dialog-list__skeleton-line--preview" />
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {activeListState?.isLoading && chats.length > 0 ? (
            <div className="dialog-list__empty">Подгружаем…</div>
          ) : !activeListHasMore && chats.length > 0 && normalizedQuery.length === 0 ? (
            <div className="dialog-list__empty">Это все чаты в папке.</div>
          ) : activeListHasMore && chats.length > 0 && normalizedQuery.length === 0 ? (
            // Ручная кнопка догрузки — на случай если auto-scroll не
            // успевает или виртуализатор/layout мешают handleScroll.
            // Когда юзер видит её — точно знает что loadMore доступен.
            <button
              type="button"
              className="dialog-list__load-more"
              onClick={() => loadMoreDialogs(activeDialogListId)}
            >
              Загрузить ещё ({chats.length})
            </button>
          ) : null}

          {!activeListState?.isLoading &&
          activeListState?.isLoaded &&
          chats.length === 0 &&
          normalizedQuery.length === 0 ? (
            <div className="dialog-list__empty dialog-list__empty--sticker">
              <LottieSticker src="/tg/tgs/EmptyFolder.json" size={110} />
              <span>В папке пока нет чатов.</span>
            </div>
          ) : null}

          {!activeListState?.isLoading &&
          filteredChats.length === 0 &&
          normalizedQuery.length > 0 ? (
            messageSearchActive ? (
              // Под заглушкой идёт блок «Сообщения» — без утки, одной строкой,
              // иначе результаты по сообщениям уезжают за экран.
              <div className="dialog-list__empty dialog-list__empty--compact">
                {serverSearchLoading || !serverResultsFresh
                  ? "Ищем чаты в Telegram…"
                  : "Чатов с таким названием не найдено."}
              </div>
            ) : (
              <div className="dialog-list__empty dialog-list__empty--sticker">
                <LottieSticker src="/tg/tgs/UtyanSearch.json" size={110} />
                <span>Чатов с таким названием не найдено.</span>
              </div>
            )
          ) : null}
          {messageSearchActive ? (
            <MessageSearchResults query={serverQuery} includeDeleted={includeDeletedInSearch} />
          ) : null}
        </div>
      </div>
      <button
        type="button"
        className="fab"
        onClick={() => {
          // Quick Switcher слушает Ctrl/Cmd+J на window — эмулируем хоткей.
          // Раньше здесь стоял «k», но с ⌘K → Command Palette (а теперь ⌘M)
          // кнопка открывала не тот компонент.
          window.dispatchEvent(
            new KeyboardEvent("keydown", { key: "j", ctrlKey: true, bubbles: true })
          );
        }}
        aria-label="Быстрый переход к чату"
        title="Быстрый переход (Ctrl+J)"
      >
        <i className="ic ic-edit" aria-hidden="true" />
      </button>
      {contextMenu && contextMenuChat
        ? createPortal(
            <div
              className="dialog-context-menu__layer"
              onClick={() => setContextMenu(null)}
              onContextMenu={(event) => {
                event.preventDefault();
                setContextMenu(null);
              }}
              role="presentation"
            >
              <ContextMenuPositioner anchorX={contextMenu.x} anchorY={contextMenu.y}>
                <button
                  type="button"
                  role="menuitem"
                  className="dialog-context-menu__item"
                  onClick={() => {
                    selectChat(contextMenu.chatId);
                    setContextMenu(null);
                  }}
                >
                  <i className="ic ic-message" aria-hidden="true" />
                  Открыть
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="dialog-context-menu__item"
                  onClick={() => {
                    previewChatDialog(contextMenu.chatId, contextMenu.x, contextMenu.y);
                    setContextMenu(null);
                  }}
                >
                  <i className="ic ic-eye1" aria-hidden="true" />
                  Предпросмотр
                </button>
                <div className="dialog-context-menu__divider" />
                <button
                  type="button"
                  role="menuitem"
                  className="dialog-context-menu__item"
                  onClick={() => {
                    togglePinned(
                      contextMenu.chatId,
                      !contextMenuIsPinnedInActiveList,
                      activeDialogListId
                    );
                    setContextMenu(null);
                  }}
                >
                  <i
                    className={contextMenuIsPinnedInActiveList ? "ic ic-pin2" : "ic ic-pin"}
                    aria-hidden="true"
                  />
                  {contextMenuIsPinnedInActiveList ? "Открепить" : "Закрепить"}
                </button>
                {contextMenuCanMoveUp ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="dialog-context-menu__item"
                    onClick={() => {
                      movePinned(contextMenu.chatId, "up", activeDialogListId);
                      setContextMenu(null);
                    }}
                  >
                    ↑ Переместить выше
                  </button>
                ) : null}
                {contextMenuCanMoveDown ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="dialog-context-menu__item"
                    onClick={() => {
                      movePinned(contextMenu.chatId, "down", activeDialogListId);
                      setContextMenu(null);
                    }}
                  >
                    ↓ Переместить ниже
                  </button>
                ) : null}
                <button
                  type="button"
                  role="menuitem"
                  className="dialog-context-menu__item"
                  onClick={() => {
                    toggleArchived(contextMenu.chatId, !contextMenuIsArchived);
                    setContextMenu(null);
                  }}
                >
                  <i className="ic ic-folder" aria-hidden="true" />
                  {contextMenuIsArchived ? "Разархивировать" : "Архивировать"}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="dialog-context-menu__item"
                  onClick={() => {
                    setChatMuted(contextMenu.chatId, !contextMenuChat.isMuted);
                    setContextMenu(null);
                  }}
                >
                  <i
                    className={contextMenuChat.isMuted ? "ic ic-unmute" : "ic ic-mute"}
                    aria-hidden="true"
                  />
                  {contextMenuChat.isMuted ? "Включить уведомления" : "Выключить уведомления"}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="dialog-context-menu__item"
                  onClick={() => {
                    toggleMarkedAsUnread(contextMenu.chatId, !contextMenuIsMarkedUnread);
                    setContextMenu(null);
                  }}
                >
                  <i
                    className={contextMenuIsMarkedUnread ? "ic ic-2checks" : "ic ic-unread"}
                    aria-hidden="true"
                  />
                  {contextMenuIsMarkedUnread ? "Пометить прочитанным" : "Пометить непрочитанным"}
                </button>
                {currentAccountId && isAdminRole ? (
                  (() => {
                    const isFrequent = frequentChats.some(
                      (entry) => entry.chatId === contextMenu.chatId
                    );
                    return (
                      <button
                        type="button"
                        role="menuitem"
                        className="dialog-context-menu__item"
                        onClick={() => {
                          void setAccountFrequentChat(
                            currentAccountId,
                            contextMenu.chatId,
                            contextMenuChat.title,
                            isFrequent ? "remove" : "add"
                          );
                          setContextMenu(null);
                        }}
                        title="Список «Переслать в Частые группы»: чаты/группы/боты, куда саппортам этого аккаунта разрешена быстрая пересылка любых сообщений."
                      >
                        <i className="ic ic-forward" aria-hidden="true" />
                        {isFrequent ? "Убрать из Частых групп" : "В Частые группы"}
                      </button>
                    );
                  })()
                ) : null}
                {contextMenuFolderChoices.length > 0 ? (
                  <>
                    <div className="dialog-context-menu__divider" />
                    <div className="dialog-context-menu__section">Папки</div>
                    {contextMenuFolderChoices.map((folder) => {
                      const isLocalFolder = folder.id.startsWith("local:");
                      const localChatIds = localFolderChatIdsByList[folder.id] ?? [];
                      const inFolder = isLocalFolder
                        ? localChatIds.includes(contextMenu.chatId)
                        : contextMenuChat.listIds.includes(folder.id);
                      return (
                        <button
                          key={folder.id}
                          type="button"
                          role="menuitem"
                          className="dialog-context-menu__item"
                          onClick={() => {
                            if (isLocalFolder) {
                              // Локальная папка живёт в нашей БД: правим её
                              // состав, а не списки TDLib.
                              void updateChatFolder(
                                folder.id,
                                inFolder
                                  ? { removeChatIds: [contextMenu.chatId] }
                                  : { addChatIds: [contextMenu.chatId] }
                              );
                              setContextMenu(null);
                              return;
                            }
                            const nextLists = inFolder
                              ? contextMenuChat.listIds.filter((id) => id !== folder.id)
                              : [...contextMenuChat.listIds, folder.id];
                            setListMembership(contextMenu.chatId, nextLists);
                            setContextMenu(null);
                          }}
                        >
                          <span className="dialog-context-menu__check">
                            {inFolder ? <i className="ic ic-check" aria-hidden="true" /> : null}
                            {folder.title}
                          </span>
                        </button>
                      );
                    })}
                  </>
                ) : null}
              </ContextMenuPositioner>
            </div>,
            document.body
          )
        : null}
      {previewChat
        ? createPortal(
        <div className="dialog-preview dialog-preview--popover" role="dialog">
          <button
            type="button"
            className="dialog-preview__backdrop"
            onClick={() => setPreviewChatId(null)}
            onContextMenu={(event) => {
              event.preventDefault();
              setPreviewChatId(null);
            }}
            onWheel={() => setPreviewChatId(null)}
            aria-label="Закрыть превью диалога"
          />
          <ContextMenuPositioner
            anchorX={previewAnchor.x}
            anchorY={previewAnchor.y}
            className="dialog-preview__panel dialog-preview__panel--popover"
            role="document"
          >
            <div className="dialog-preview__header">
              <div className="dialog-preview__summary">
                <ChatAvatar
                  title={previewChat.isSavedMessages ? "Избранное" : previewChat.title}
                  avatarUrl={previewChat.avatarUrl}
                  className={`dialog-preview__avatar ${avatarGradientClass(previewChat.chatId)}`}
                  savedMessages={previewChat.isSavedMessages ?? false}
                />
                <div>
                  <strong>{previewChat.isSavedMessages ? "Избранное" : previewChat.title}</strong>
                  <span>Тихий просмотр — без отметки «прочитано»</span>
                </div>
              </div>
              <div className="dialog-preview__header-actions">
                <button
                  type="button"
                  className={
                    previewTranslated
                      ? "icon-button dialog-preview__translate dialog-preview__translate--on"
                      : "icon-button dialog-preview__translate"
                  }
                  onClick={() => setChatTranslation(previewChat.chatId, !previewTranslated)}
                  title={
                    previewTranslated
                      ? "Показать оригинал"
                      : `Перевести чат (${previewTranslationLang})`
                  }
                  aria-label={
                    previewTranslated
                      ? "Показать оригинал"
                      : `Перевести чат на ${previewTranslationLang}`
                  }
                  aria-pressed={previewTranslated}
                >
                  <i className="ic ic-translate" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => setPreviewChatId(null)}
                  aria-label="Закрыть превью"
                >
                  <i className="ic ic-close" aria-hidden="true" />
                </button>
              </div>
            </div>
            <div className="dialog-preview__body" ref={previewBodyRef}>
              {previewIsLoading && previewMessages.length === 0 ? (
                <div className="dialog-preview__empty">Загружаем…</div>
              ) : previewMessages.length === 0 ? (
                <div className="dialog-preview__empty">История пока пуста.</div>
              ) : (
                <div className="dialog-preview__messages">
                  {previewMessages.slice(-24).map((message, index, window) => {
                    const previous = window[index - 1];
                    const inGroupWithPrev =
                      Boolean(previous) &&
                      previous!.outgoing === message.outgoing &&
                      previous!.senderId === message.senderId;
                    const translation =
                      translationsByMessage[translationKey(previewChat.chatId, message.id)];
                    return (
                      <div
                        key={message.id}
                        className={[
                          "dialog-preview__message",
                          message.outgoing ? "dialog-preview__message--outgoing" : "",
                          !inGroupWithPrev ? "dialog-preview__message--first" : ""
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        {!message.outgoing && !inGroupWithPrev && previewChat.isGroupChat ? (
                          <strong className="dialog-preview__message-sender">
                            {message.senderName ?? "Собеседник"}
                          </strong>
                        ) : null}
                        {message.outgoing && message.internalAuthorName && !inGroupWithPrev ? (
                          <strong className="dialog-preview__message-sender">
                            Вы · {message.internalAuthorName}
                          </strong>
                        ) : null}
                        {!message.isDeleted &&
                        (message.content.kind === "photo" ||
                          message.content.kind === "video") ? (
                          (() => {
                            // Тонкий превью-режим: показываем миниатюру медиа,
                            // а не только лейбл «Фото/Видео/…». Без миниатюр
                            // юзер жалуется, что отправленные фото в
                            // «предпросмотре» видны только текстом.
                            const previewUrl =
                              message.content.thumbnailUrl ?? message.content.url ?? "";
                            return (
                              <>
                                {previewUrl ? (
                                  <img
                                    src={previewUrl}
                                    alt={message.content.caption ?? message.text}
                                    className="dialog-preview__message-media"
                                    loading="lazy"
                                    decoding="async"
                                  />
                                ) : null}
                                <span className="dialog-preview__message-text">
                                  {message.content.caption ?? message.text}
                                </span>
                              </>
                            );
                          })()
                        ) : (
                          <span className="dialog-preview__message-text">
                            {message.isDeleted ? "Сообщение удалено" : message.text}
                          </span>
                        )}
                        {translation && !message.isDeleted ? (
                          <span className="dialog-preview__message-translation">
                            <i className="ic ic-translate" aria-hidden="true" />
                            {translation.text}
                          </span>
                        ) : null}
                        <span className="dialog-preview__message-time">
                          {formatTime(message.occurredAt, previewTimeZone)}
                          {(() => {
                            // Галочки прочтения — те же, что в ленте (1/2 галочки).
                            const status = message.isDeleted
                              ? null
                              : messageStatusLabel(
                                  message.outgoing,
                                  message.id,
                                  message.occurredAt,
                                  message.isPending,
                                  previewChat.lastReadOutboxMessageId,
                                  previewChat.lastReadOutboxAt
                                );
                            if (!status) return null;
                            return (
                              <i
                                className={
                                  status === "✓✓"
                                    ? "ic ic-2checks dialog-preview__message-status dialog-preview__message-status--read"
                                    : status === "✓"
                                      ? "ic ic-1check dialog-preview__message-status"
                                      : "ic ic-sending dialog-preview__message-status"
                                }
                                aria-label={
                                  status === "✓✓" ? "Прочитано" : status === "✓" ? "Отправлено" : "Отправляется"
                                }
                                title={
                                  status === "✓✓" ? "Прочитано" : status === "✓" ? "Отправлено" : "Отправляется"
                                }
                              />
                            );
                          })()}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="dialog-preview__footer">
              <button
                type="button"
                className="button button--primary"
                onClick={() => {
                  selectChat(previewChat.chatId);
                  setPreviewChatId(null);
                }}
              >
                <i className="ic ic-message" aria-hidden="true" /> Открыть чат
              </button>
            </div>
          </ContextMenuPositioner>
        </div>
          ,
          document.body
        )
        : null}
      {folderTabMenu
        ? createPortal(
            <div
              className="dialog-context-menu__layer"
              onClick={() => setFolderTabMenu(null)}
              onContextMenu={(event) => {
                event.preventDefault();
                setFolderTabMenu(null);
              }}
              role="presentation"
            >
              <ContextMenuPositioner anchorX={folderTabMenu.x} anchorY={folderTabMenu.y}>
                {currentAccountId ? (
                  <>
                    <button
                      type="button"
                      role="menuitem"
                      className="dialog-context-menu__item"
                      onClick={() => {
                        openPushLaunch(folderTabMenu.listId, folderTabMenu.title);
                        setFolderTabMenu(null);
                      }}
                      title="ИИ-пропуш: индивидуальные follow-up юзерам папки, которые прочитали наше последнее сообщение и не ответили"
                    >
                      <i className="ic ic-send" aria-hidden="true" />
                      Пуш по папке (ИИ)
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="dialog-context-menu__item"
                      onClick={() => {
                        openPushLaunch(folderTabMenu.listId, folderTabMenu.title, "scripts");
                        setFolderTabMenu(null);
                      }}
                      title="Пуш своими текстами: заготовленные скрипты по кругу юзерам папки, которые прочитали наше последнее сообщение и не ответили"
                    >
                      <i className="ic ic-send" aria-hidden="true" />
                      Пуш по папке (свои скрипты)
                    </button>
                    <div className="dialog-context-menu__divider" />
                  </>
                ) : null}
                {folderTabMenu.listId.startsWith("local:") ? (
                  <>
                    <button
                      type="button"
                      role="menuitem"
                      className="dialog-context-menu__item"
                      onClick={() => {
                        setFolderTopUp({
                          listId: folderTabMenu.listId,
                          title: folderTabMenu.title
                        });
                        setFolderTabMenu(null);
                      }}
                    >
                      Добрать чаты
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="dialog-context-menu__item"
                      onClick={() => {
                        const targetListId = folderTabMenu.listId;
                        const localFolder = localFolders.find(
                          (folder) => `local:${folder.folderId}` === targetListId
                        );
                        const initialIds = localFolder
                          ? localFolder.includedChatIds
                          : Object.values(chatsById)
                              .filter((chat) => chat.listIds.includes(targetListId))
                              .map((chat) => chat.chatId);
                        setFolderEditor(targetListId);
                        setFolderEditorTitle(folderTabMenu.title);
                        setFolderEditorChatIds(new Set(initialIds));
                        folderEditorInitialIds.current = new Set(initialIds);
                        setFolderEditorChatLimit(localFolder?.chatLimit ?? null);
                        setFolderTabMenu(null);
                      }}
                    >
                      Переименовать / изменить состав
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="dialog-context-menu__item"
                      disabled={folderConvertBusy !== null}
                      onClick={() => {
                        void convertFolder(folderTabMenu.listId, folderTabMenu.title);
                        setFolderTabMenu(null);
                      }}
                      title="Создать такую же папку в Telegram (виден в официальных клиентах) и удалить папку клиента. Состав и название сохраняются."
                    >
                      {folderConvertBusy === folderTabMenu.listId
                        ? "Конвертируем…"
                        : "Сделать папкой Telegram"}
                    </button>
                    <div className="dialog-context-menu__divider" />
                    <button
                      type="button"
                      role="menuitem"
                      className="dialog-context-menu__item dialog-context-menu__item--danger"
                      onClick={() => {
                        if (
                          window.confirm(
                            `Удалить папку «${folderTabMenu.title}»? Сами чаты останутся, удалится только папка.`
                          )
                        ) {
                          void deleteChatFolder(folderTabMenu.listId);
                        }
                        setFolderTabMenu(null);
                      }}
                    >
                      Удалить папку
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      role="menuitem"
                      className="dialog-context-menu__item"
                      disabled={folderConvertBusy !== null}
                      onClick={() => {
                        void convertFolder(folderTabMenu.listId, folderTabMenu.title);
                        setFolderTabMenu(null);
                      }}
                      title="Создать папку клиента с тем же названием и составом и удалить папку Telegram. Папкой клиента можно управлять здесь: состав, добор чатов, лимит."
                    >
                      {folderConvertBusy === folderTabMenu.listId
                        ? "Конвертируем…"
                        : "Сделать папкой клиента"}
                    </button>
                    <div className="dialog-context-menu__item dialog-context-menu__item--disabled">
                      Папка пришла из Telegram. Состав и название —<br />
                      в основном клиенте TG или после конвертации.
                    </div>
                  </>
                )}
                {/* Привязка папки к линиям саппортов (раунд 46): только для
                    менеджеров. Клики по чекбоксам не закрывают меню —
                    админ отмечает несколько линий подряд. */}
                {isAdminRole && folderLines ? (
                  <>
                    <div className="dialog-context-menu__divider" />
                    <div className="dialog-context-menu__section">Привязка к линиям</div>
                    {!folderLines.enabled ? (
                      <div className="dialog-context-menu__item dialog-context-menu__item--disabled">
                        Разделение папок по линиям выключено<br />
                        (Настройки → Линии и ЗП).
                      </div>
                    ) : folderLines.lines.length === 0 ? (
                      <div className="dialog-context-menu__item dialog-context-menu__item--disabled">
                        Нет линий с гео этого аккаунта<br />
                        (Настройки → Линии и ЗП → гео линии;<br />
                        Настройки → Гео → гео аккаунта).
                      </div>
                    ) : (
                      folderLines.lines.map((line) => {
                        const bound = folderLines.bindings[folderTabMenu.listId] ?? [];
                        const checked = bound.includes(line.lineId);
                        // Линия чужого гео: сервер отдаёт её только пока она
                        // привязана к какой-то папке аккаунта — снять можно,
                        // поставить заново нельзя.
                        if (line.outOfGeo && !checked) {
                          return null;
                        }
                        return (
                          <button
                            key={line.lineId}
                            type="button"
                            role="menuitemcheckbox"
                            aria-checked={checked}
                            className="dialog-context-menu__item"
                            onClick={(event) => {
                              event.stopPropagation();
                              const next = checked
                                ? bound.filter((id) => id !== line.lineId)
                                : [...bound, line.lineId];
                              void setFolderLineBinding(folderTabMenu.listId, next);
                            }}
                            title={
                              checked
                                ? "Папку видят саппорты этой линии"
                                : "Показать папку саппортам этой линии"
                            }
                          >
                            {checked ? "☑ " : "☐ "}
                            {line.title}
                            {line.outOfGeo ? " (другое гео)" : ""}
                          </button>
                        );
                      })
                    )}
                    {folderLines.enabled &&
                    (folderLines.bindings[folderTabMenu.listId] ?? []).length === 0 ? (
                      <div className="dialog-context-menu__item dialog-context-menu__item--disabled">
                        Без отметок папка видна всем линиям.
                      </div>
                    ) : null}
                  </>
                ) : null}
              </ContextMenuPositioner>
            </div>,
            document.body
          )
        : null}
      {pushLaunch && currentAccountId ? (
        <PushJobModal
          accountId={currentAccountId}
          listId={pushLaunch.listId}
          listTitle={pushLaunch.title}
          mode={pushLaunch.mode}
          onClose={closePushLaunch}
        />
      ) : null}
      {folderEditor
        ? createPortal(
            <FolderEditorModal
              mode={folderEditor === "new" ? "create" : "edit"}
              title={folderEditorTitle}
              onTitleChange={setFolderEditorTitle}
              selectedChatIds={folderEditorChatIds}
              onToggleChat={(chatId) => {
                setFolderEditorChatIds((prev) => {
                  const next = new Set(prev);
                  if (next.has(chatId)) {
                    next.delete(chatId);
                  } else {
                    next.add(chatId);
                  }
                  return next;
                });
              }}
              sourceFolders={availableLists
                // Архив пропускаем — в нём не «выбирают чаты для папки».
                .filter((dialogList) => dialogList.id !== "archive")
                .map((dialogList) => ({ id: dialogList.id, title: dialogList.title }))}
              // Источники автонабора — только списки, которые считает воркер:
              // «Общие» и папки Telegram. Локальные папки сюда не идут, их
              // состав SPA и так знает целиком.
              autoPickSources={autoPickSources}
              onAutoPicked={(chatIds) => {
                setFolderEditorChatIds((prev) => {
                  const next = new Set(prev);
                  for (const chatId of chatIds) {
                    next.add(chatId);
                  }
                  return next;
                });
              }}
              allChats={folderEditorAllChats}
              localFolderChatIds={localFolderChatIdsByList}
              resolveChatTitle={resolveFolderEditorChatTitle}
              // В кэше клиента изначально лежит только первая страница чатов
              // из main (≈50). Прокидываем сюда состояние пагинации
              // main-листа и колбэк догрузки — пользователь сможет дотянуть
              // полный список из Telegram прямо из модалки.
              backendHasMore={dialogHasMoreById["main"] ?? true}
              backendIsLoading={dialogListStateById["main"]?.isLoading ?? false}
              requestBackendMore={requestMainDialogs}
              chatLimit={folderEditorChatLimit}
              onChatLimitChange={setFolderEditorChatLimit}
              onCancel={() => setFolderEditor(null)}
              onSubmit={() => {
                const ids = Array.from(folderEditorChatIds);
                if (folderEditor === "new") {
                  void createChatFolder(folderEditorTitle, ids, folderEditorChatLimit);
                } else {
                  const initial = folderEditorInitialIds.current;
                  const selected = new Set(ids);
                  void updateChatFolder(folderEditor, {
                    title: folderEditorTitle,
                    addChatIds: ids.filter((id) => !initial.has(id)),
                    removeChatIds: Array.from(initial).filter((id) => !selected.has(id)),
                    chatLimit: folderEditorChatLimit
                  });
                }
                setFolderEditor(null);
              }}
            />,
            document.body
          )
        : null}
      {folderTopUp
        ? createPortal(
            <FolderTopUpModal
              folderTitle={folderTopUp.title}
              sources={autoPickSources}
              room={(() => {
                const folder = localFolders.find(
                  (entry) => `local:${entry.folderId}` === folderTopUp.listId
                );
                if (!folder || !folder.chatLimit || folder.chatLimit <= 0) {
                  return null;
                }
                return Math.max(0, folder.chatLimit - folder.includedChatIds.length);
              })()}
              onPicked={async (chatIds) => {
                const added = await appendChatsToLocalFolder(folderTopUp.listId, chatIds);
                return added;
              }}
              onClose={() => setFolderTopUp(null)}
            />,
            document.body
          )
        : null}
    </aside>
  );
});
DialogList.displayName = "DialogList";

// Псевдо-источник редактора папки: показывает отмеченные чаты (текущий
// состав папки), чтобы лишние можно было снять, не разыскивая их в «Общих».
const SELECTED_SOURCE_ID = "__selected";
type FolderEditorChatEntry = { chatId: string; title: string };

interface FolderEditorModalProps {
  mode: "create" | "edit";
  title: string;
  onTitleChange(value: string): void;
  selectedChatIds: Set<string>;
  onToggleChat(chatId: string): void;
  allChats: ChatViewModel[];
  // Список папок-источников: пользователь выбирает одну, и список чатов
  // сужается до чатов, состоящих в этой папке. По умолчанию — "main"
  // («Общие»). Это и есть фильтр «новые/общие» из ТЗ.
  sourceFolders: Array<{ id: string; title: string }>;
  // Состав локальных папок (listId "local:<id>" → chatIds): у чатов в
  // listIds локальных папок нет, поэтому источник «local:*» без этой карты
  // всегда был пустым — и снять чат с папки можно было только найдя его в
  // «Общих» (при 10k чатов это «не убирается никогда»).
  localFolderChatIds: Record<string, string[]>;
  // Заголовок чата по id для тех, кого нет в allChats (ещё не догружен /
  // архив / болванка).
  resolveChatTitle(chatId: string): string;
  // Подмножество sourceFolders, из которого умеет набирать воркер («Общие» и
  // папки Telegram). Если выбран источник вне этого списка, автонабор
  // недоступен — набирать из локальной папки нечего.
  autoPickSources: Array<{ id: string; title: string }>;
  // Автонабор вернул чаты: дописываем их к отмеченным.
  onAutoPicked(chatIds: string[]): void;
  // null = без лимита, число > 0 = лимит.
  chatLimit: number | null;
  onChatLimitChange(value: number | null): void;
  backendHasMore: boolean;
  backendIsLoading: boolean;
  requestBackendMore(): void;
  onCancel(): void;
  onSubmit(): void;
}

const FolderEditorModal = ({
  mode,
  title,
  onTitleChange,
  selectedChatIds,
  onToggleChat,
  allChats,
  sourceFolders,
  localFolderChatIds,
  resolveChatTitle,
  autoPickSources,
  onAutoPicked,
  chatLimit,
  onChatLimitChange,
  backendHasMore,
  backendIsLoading,
  requestBackendMore,
  onCancel,
  onSubmit
}: FolderEditorModalProps) => {
  // Авто-цикл: пока worker говорит hasMore=true, дёргаем requestBackendMore.
  // Эффект перезапускается на каждый ответ worker'а (backendIsLoading
  // переключается false→true→false) и, если ещё есть что подгружать, шлёт
  // следующий запрос. Останавливается когда worker отдаст hasMore=false.
  // Пауза 600 мс между страницами — как у авто-докрутки основного списка:
  // без неё модалка долбила worker страница-за-страницей вплотную и на
  // 10k-аккаунте замораживала и его, и UI.
  // Backoff по застою (как у авто-докрутки списка): если «Общие» не выросли
  // после ответа, следующий запрос через 1.2с → 2.4с → … → 30с. Без этого
  // открытая модалка на папке с hasMore=true навсегда долбила воркер раз в
  // 600мс (прод 2026-10-15).
  const mainCount = useRealtimeStore((state) => state.chatIdsByList["main"]?.length ?? 0);
  const modalBackoffRef = useRef<{ maxCount: number; stalls: number }>({
    maxCount: -1,
    stalls: 0
  });
  useEffect(() => {
    if (!backendHasMore || backendIsLoading) {
      return;
    }
    const backoff = modalBackoffRef.current;
    if (mainCount > backoff.maxCount) {
      backoff.maxCount = mainCount;
      backoff.stalls = 0;
    } else {
      backoff.stalls += 1;
    }
    const delay = Math.min(600 * 2 ** backoff.stalls, 30_000);
    const timer = window.setTimeout(() => {
      requestBackendMore();
    }, delay);
    return () => window.clearTimeout(timer);
  }, [backendHasMore, backendIsLoading, mainCount, requestBackendMore]);
  const [query, setQuery] = useState("");
  const trimmedQuery = query.trim().toLowerCase();
  // Пагинация по 10: иначе на аккаунте с 10k чатов диалог рендерил всех и
  // лагал, а длинные названия в узком окне обрезались.
  const PAGE_SIZE = 10;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  // Источник чатов: "main" по умолчанию (показываем все из «Общие»).
  // Может быть "main" | "archive" | "folder:<n>" — см. listIds в ChatViewModel.
  // В режиме правки открываем сразу на «Отмеченных» — это текущий состав
  // папки, из которого чаще всего и убирают лишнее.
  const defaultSourceFolderId =
    mode === "edit" && selectedChatIds.size > 0
      ? SELECTED_SOURCE_ID
      : (sourceFolders.find((folder) => folder.id === "main")?.id ??
        sourceFolders[0]?.id ??
        "main");
  const [sourceFolderId, setSourceFolderId] = useState<string>(defaultSourceFolderId);
  const chatById = useMemo(() => {
    const map = new Map<string, ChatViewModel>();
    for (const chat of allChats) {
      map.set(chat.chatId, chat);
    }
    return map;
  }, [allChats]);
  const chatsInSource = useMemo((): FolderEditorChatEntry[] => {
    const fromIds = (ids: readonly string[]): FolderEditorChatEntry[] =>
      ids.map((chatId) => {
        const known = chatById.get(chatId);
        return known
          ? { chatId, title: known.title }
          : { chatId, title: resolveChatTitle(chatId) };
      });
    if (sourceFolderId === SELECTED_SOURCE_ID) {
      return fromIds(Array.from(selectedChatIds));
    }
    if (sourceFolderId.startsWith("local:")) {
      return fromIds(localFolderChatIds[sourceFolderId] ?? []);
    }
    return allChats
      .filter((chat) => chat.listIds.includes(sourceFolderId))
      .map((chat) => ({ chatId: chat.chatId, title: chat.title }));
  }, [allChats, chatById, localFolderChatIds, resolveChatTitle, selectedChatIds, sourceFolderId]);
  const filteredChats = useMemo(() => {
    if (trimmedQuery.length === 0) {
      return chatsInSource;
    }
    return chatsInSource.filter((chat) =>
      chat.title.toLowerCase().includes(trimmedQuery)
    );
  }, [chatsInSource, trimmedQuery]);
  const visibleChats = filteredChats.slice(0, visibleCount);
  const hasMore = filteredChats.length > visibleChats.length;
  // Смена поиска возвращает счётчик на первую страницу.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [trimmedQuery]);
  // Смена источника тоже сбрасывает пагинацию — иначе при переключении
  // с папки на 200 чатов на папку на 5 пользователь увидит «показано
  // 5 из 5», но visibleCount останется большим и при следующей смене
  // на крупную папку не отрисуется первая страница.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [sourceFolderId]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        onCancel();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  // Стабильный массив отмеченного для автонабора: Set меняется по месту,
  // поэтому материализуем его один раз на изменение размера/содержимого.
  const selectedChatIdList = useMemo(
    () => Array.from(selectedChatIds),
    [selectedChatIds]
  );
  const overLimit =
    chatLimit !== null && chatLimit > 0 && selectedChatIds.size > chatLimit;
  const canSubmit = title.trim().length > 0 && !overLimit;

  return (
    <div
      className="folder-editor"
      role="dialog"
      aria-modal="true"
      aria-label={mode === "create" ? "Создать папку" : "Изменить папку"}
    >
      <button
        type="button"
        className="folder-editor__backdrop"
        onClick={onCancel}
        aria-label="Закрыть"
      />
      <div className="folder-editor__panel">
        <div className="folder-editor__header">
          <strong>{mode === "create" ? "Новая папка" : "Изменить папку"}</strong>
          <button
            type="button"
            className="icon-button"
            onClick={onCancel}
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>
        <label className="folder-editor__row">
          <span>Название</span>
          <input
            type="text"
            value={title}
            onChange={(event) => onTitleChange(event.target.value)}
            maxLength={64}
            autoFocus
            placeholder="Например: Поддержка"
          />
        </label>
        <label className="folder-editor__row">
          <span>Лимит чатов</span>
          <div className="folder-editor__limit">
            <input
              type="number"
              min={0}
              max={10000}
              step={1}
              value={chatLimit ?? ""}
              onChange={(event) => {
                const raw = event.target.value;
                if (raw === "") {
                  onChatLimitChange(null);
                  return;
                }
                const parsed = Math.max(0, Math.min(10000, Math.floor(Number(raw))));
                onChatLimitChange(Number.isFinite(parsed) && parsed > 0 ? parsed : null);
              }}
              placeholder="без лимита"
              aria-label="Максимум чатов в этой папке"
            />
            {chatLimit && chatLimit > 0 ? (
              <span className="folder-editor__limit-hint">
                {selectedChatIds.size} / {chatLimit}
              </span>
            ) : (
              <span className="folder-editor__limit-hint">
                {selectedChatIds.size} выбрано
              </span>
            )}
          </div>
        </label>
        <label className="folder-editor__row">
          <span>Источник чатов</span>
          <select
            value={sourceFolderId}
            onChange={(event) => setSourceFolderId(event.target.value)}
            aria-label="Папка-источник"
          >
            <option value={SELECTED_SOURCE_ID}>
              Отмеченные ({selectedChatIds.size})
            </option>
            {sourceFolders.map((folder) => (
              <option key={folder.id} value={folder.id}>
                {folder.title}
              </option>
            ))}
          </select>
        </label>
        <FolderAutoPick
          sourceListId={sourceFolderId}
          canPick={autoPickSources.some((folder) => folder.id === sourceFolderId)}
          room={
            chatLimit && chatLimit > 0
              ? Math.max(0, chatLimit - selectedChatIds.size)
              : null
          }
          excludeChatIds={selectedChatIdList}
          onPicked={onAutoPicked}
        />
        <div className="folder-editor__hint">
          Ниже — ручной выбор чатов. Автонабор отмечает найденные чаты сам;
          снять лишние можно тут же.
        </div>
        <label className="folder-editor__row">
          <span>Поиск по чатам</span>
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Поиск…"
          />
        </label>
        <div className="folder-editor__hint">
          Отмечено: {selectedChatIds.size}. Показано {visibleChats.length} из {filteredChats.length}
          {filteredChats.length !== allChats.length ? ` (всего ${allChats.length})` : ""}.
        </div>
        <div className="folder-editor__chats">
          {visibleChats.map((chat) => {
            const checked = selectedChatIds.has(chat.chatId);
            return (
              <label key={chat.chatId} className="folder-editor__chat">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggleChat(chat.chatId)}
                />
                <span className="folder-editor__chat-title" title={chat.title}>
                  {chat.title}
                </span>
              </label>
            );
          })}
          {visibleChats.length === 0 ? (
            <div className="folder-editor__empty">Ничего не найдено.</div>
          ) : null}
          {hasMore ? (
            <button
              type="button"
              className="folder-editor__more"
              onClick={() => setVisibleCount((value) => value + PAGE_SIZE)}
            >
              Показать ещё ({filteredChats.length - visibleChats.length})
            </button>
          ) : backendHasMore ? (
            <button
              type="button"
              className="folder-editor__more"
              onClick={() => {
                requestBackendMore();
                // Сразу прибавляем visibleCount: когда новые чаты
                // прилетят, они будут уже отрисованы без дополнительного
                // нажатия «Показать ещё».
                setVisibleCount((value) => value + PAGE_SIZE);
              }}
              disabled={backendIsLoading}
            >
              {backendIsLoading
                ? "Загружаем из Telegram…"
                : "Загрузить ещё чаты из Telegram"}
            </button>
          ) : null}
        </div>
        <div className="folder-editor__actions">
          <button
            type="button"
            className="button button--ghost"
            onClick={onCancel}
          >
            Отмена
          </button>
          <button
            type="button"
            className="button button--primary"
            onClick={onSubmit}
            disabled={!canSubmit}
          >
            {mode === "create" ? "Создать" : "Сохранить"}
          </button>
        </div>
      </div>
    </div>
  );
};

interface FolderAutoPickProps {
  // Список-источник (id из availableLists). Управляется родителем: в
  // редакторе папок это тот же селект, что сужает ручной список чатов.
  sourceListId: string;
  // false — источник не поддерживается автонабором (локальная папка).
  canPick: boolean;
  // Сколько чатов ещё влезает по лимиту папки; null = лимита нет.
  room: number | null;
  // Уже набранное/отмеченное, но ещё не сохранённое в папку: сервер про эти
  // чаты не знает, и без явного исключения повторный подбор вернул бы их же.
  excludeChatIds: string[];
  onPicked(chatIds: string[]): void;
}

/**
 * Автонабор чатов из папки-источника. Сам подбор делает воркер (команда
 * chatFolder.pickChats): он идёт по источнику в порядке Telegram — свежие
 * первыми — и берёт только чаты, которых нет ни в одной другой папке,
 * включая локальные папки нашего клиента. Здесь — только форма и статус.
 */
const FolderAutoPick = ({
  sourceListId,
  canPick,
  room,
  excludeChatIds,
  onPicked
}: FolderAutoPickProps) => {
  const timeZone: TimeZonePref = useUiPrefs((state) => state.timeZone);
  const pickFolderChats = useRealtimeStore((state) => state.pickFolderChats);
  const clearFolderPick = useRealtimeStore((state) => state.clearFolderPick);
  const folderPick = useRealtimeStore((state) => state.folderPick);
  const [count, setCount] = useState<number | null>(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [timeFrom, setTimeFrom] = useState("");
  const [timeTo, setTimeTo] = useState("");
  const [onlyUnread, setOnlyUnread] = useState(true);
  // Подбор мы инициировали и ещё не отдали наверх. Без этих ссылок результат
  // из соседней вкладки (события идут broadcast'ом) или повторный рендер
  // отметили бы чаты ещё раз.
  const armedRef = useRef(false);
  const handledRef = useRef<string | null>(null);

  useEffect(() => {
    // Модалку закрыли/открыли — прошлый результат не наш.
    return () => {
      clearFolderPick();
    };
  }, [clearFolderPick]);

  useEffect(() => {
    if (!folderPick || folderPick.status !== "done" || !armedRef.current) {
      return;
    }
    if (handledRef.current === folderPick.requestId) {
      return;
    }
    handledRef.current = folderPick.requestId;
    armedRef.current = false;
    onPicked(folderPick.chatIds);
  }, [folderPick, onPicked]);

  const isPending = folderPick?.status === "pending";
  const timeWindowFilled = timeFrom.length > 0 && timeTo.length > 0;
  const timeWindowPartial =
    (timeFrom.length > 0) !== (timeTo.length > 0);
  const requested = count ?? 0;
  const effectiveCount =
    room !== null && room >= 0 ? Math.min(requested, room) : requested;

  const submit = (): void => {
    if (effectiveCount <= 0) {
      return;
    }
    armedRef.current = true;
    // Даты вводятся как «стенные часы» пояса саппорта — переводим их в UTC
    // здесь, через единый модуль дат (lib/datetime.ts).
    const from = dateFrom ? wallClockToUtcIso(`${dateFrom}T00:00`, timeZone) : null;
    const to = dateTo ? wallClockToUtcIso(`${dateTo}T23:59`, timeZone) : null;
    const toMinutes = (value: string): number | undefined => {
      const match = /^(\d{2}):(\d{2})$/.exec(value);
      if (!match) {
        return undefined;
      }
      return Number(match[1]) * 60 + Number(match[2]);
    };
    pickFolderChats({
      sourceListId,
      limit: effectiveCount,
      ...(excludeChatIds.length > 0 ? { excludeChatIds } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
      ...(timeWindowFilled
        ? {
            dayFromMinutes: toMinutes(timeFrom) ?? 0,
            dayToMinutes: toMinutes(timeTo) ?? 0,
            timeZoneOffsetMinutes: timeZoneOffsetMinutes(timeZone)
          }
        : {}),
      onlyUnread
    });
  };

  return (
    <div className="folder-autopick">
      <div className="folder-autopick__title">Набрать чаты автоматически</div>
      {canPick ? null : (
        <div className="folder-autopick__note">
          Автонабор работает только из «Общих» и папок Telegram — выберите их
          в поле «Источник чатов».
        </div>
      )}
      <label className="folder-editor__row">
        <span>Сколько набрать</span>
        <input
          type="number"
          min={1}
          max={10000}
          step={1}
          value={count ?? ""}
          onChange={(event) => {
            const raw = event.target.value;
            if (raw === "") {
              setCount(null);
              return;
            }
            const parsed = Math.max(1, Math.min(10000, Math.floor(Number(raw))));
            setCount(Number.isFinite(parsed) ? parsed : null);
          }}
          placeholder="например 500"
          disabled={!canPick}
        />
      </label>
      <div className="folder-editor__row folder-autopick__range">
        <span>Даты (необязательно)</span>
        <div className="folder-autopick__inputs">
          <input
            type="date"
            value={dateFrom}
            onChange={(event) => setDateFrom(event.target.value)}
            aria-label="Дата с"
            disabled={!canPick}
          />
          <input
            type="date"
            value={dateTo}
            onChange={(event) => setDateTo(event.target.value)}
            aria-label="Дата по"
            disabled={!canPick}
          />
        </div>
      </div>
      <div className="folder-editor__row folder-autopick__range">
        <span>Время суток (необязательно)</span>
        <div className="folder-autopick__inputs">
          <input
            type="time"
            value={timeFrom}
            onChange={(event) => setTimeFrom(event.target.value)}
            aria-label="Время с"
            disabled={!canPick}
          />
          <input
            type="time"
            value={timeTo}
            onChange={(event) => setTimeTo(event.target.value)}
            aria-label="Время по"
            disabled={!canPick}
          />
        </div>
      </div>
      <div className="folder-autopick__hint">
        Фильтр по времени последнего сообщения, часовой пояс —{" "}
        {timeZoneShortLabel(timeZone)}. Окно через полночь допустимо: 22:00 → 05:00.
        {room !== null ? ` Свободно по лимиту папки: ${room}.` : ""}
      </div>
      <label className="folder-editor__row folder-autopick__toggle">
        <span>Только непрочитанные</span>
        <input
          type="checkbox"
          checked={onlyUnread}
          onChange={(event) => setOnlyUnread(event.target.checked)}
          disabled={!canPick}
          style={{ width: "auto" }}
        />
      </label>
      <div className="folder-autopick__hint">
        Берутся только чаты, где собеседник написал и ему ещё не ответили (или
        чат помечен непрочитанным). Снимите галочку, чтобы набирать и прочитанные.
      </div>
      <button
        type="button"
        className="button button--ghost folder-autopick__submit"
        onClick={submit}
        disabled={!canPick || isPending || effectiveCount <= 0 || timeWindowPartial}
      >
        {isPending ? "Подбираем…" : "Подобрать"}
      </button>
      {timeWindowPartial ? (
        <div className="folder-autopick__note">
          Укажите обе границы времени суток или очистите обе.
        </div>
      ) : null}
      {folderPick?.status === "error" ? (
        <div className="folder-autopick__note">{folderPick.message}</div>
      ) : null}
      {folderPick?.status === "done" ? (
        <div className="folder-autopick__note">
          Найдено {folderPick.chatIds.length} из запрошенных{" "}
          {folderPick.requestedLimit}. Просмотрено чатов источника:{" "}
          {folderPick.scannedCount}, отсеяно как «уже в другой папке»:{" "}
          {folderPick.skippedInOtherFoldersCount}.
          {folderPick.chatIds.length < folderPick.requestedLimit && !folderPick.exhausted
            ? " Источник просмотрен не до конца — нажмите «Подобрать» ещё раз, чтобы добрать остаток."
            : ""}
        </div>
      ) : null}
    </div>
  );
};

interface FolderTopUpModalProps {
  folderTitle: string;
  sources: Array<{ id: string; title: string }>;
  // Свободные слоты по лимиту папки; null = лимита нет.
  room: number | null;
  // Возвращает, сколько чатов реально добавилось.
  onPicked(chatIds: string[]): Promise<number>;
  onClose(): void;
}

/** «Добрать чаты» из ПКМ-меню локальной папки: источник + количество + рамки. */
const FolderTopUpModal = ({
  folderTitle,
  sources,
  room,
  onPicked,
  onClose
}: FolderTopUpModalProps) => {
  const [sourceListId, setSourceListId] = useState<string>(
    () => sources.find((folder) => folder.id !== "main")?.id ?? sources[0]?.id ?? "main"
  );
  const [addedCount, setAddedCount] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  // Всё, что этот сеанс уже добрал: сохранение в БД асинхронное, и повторный
  // «Подобрать» до его завершения вернул бы те же чаты.
  const [pickedSoFar, setPickedSoFar] = useState<string[]>([]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handlePicked = useCallback(
    (chatIds: string[]) => {
      setPickedSoFar((prev) => [...prev, ...chatIds]);
      setIsSaving(true);
      void onPicked(chatIds)
        .then((added) => setAddedCount((prev) => (prev ?? 0) + added))
        .finally(() => setIsSaving(false));
    },
    [onPicked]
  );

  return (
    <div
      className="folder-editor"
      role="dialog"
      aria-modal="true"
      aria-label={`Добрать чаты в папку ${folderTitle}`}
    >
      <button
        type="button"
        className="folder-editor__backdrop"
        onClick={onClose}
        aria-label="Закрыть"
      />
      <div className="folder-editor__panel">
        <div className="folder-editor__header">
          <strong>Добрать чаты — «{folderTitle}»</strong>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </div>
        <label className="folder-editor__row">
          <span>Источник чатов</span>
          <select
            value={sourceListId}
            onChange={(event) => setSourceListId(event.target.value)}
            aria-label="Папка-источник"
          >
            {sources.map((folder) => (
              <option key={folder.id} value={folder.id}>
                {folder.title}
              </option>
            ))}
          </select>
        </label>
        <FolderAutoPick
          sourceListId={sourceListId}
          canPick={sources.some((folder) => folder.id === sourceListId)}
          room={room}
          excludeChatIds={pickedSoFar}
          onPicked={handlePicked}
        />
        {isSaving ? (
          <div className="folder-autopick__note">Добавляем чаты в папку…</div>
        ) : null}
        {addedCount !== null && !isSaving ? (
          <div className="folder-autopick__note">
            {addedCount > 0
              ? `Добавлено чатов: ${addedCount}.`
              : "Новых чатов не добавилось: подходящих не нашлось или папка уже заполнена по лимиту."}
          </div>
        ) : null}
        <div className="folder-editor__actions">
          <button type="button" className="button button--ghost" onClick={onClose}>
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
};
